"use client";
import { webRegSemesterSelection } from '@/app/lib/webRegSemester';

import { restoreCourseListEligibility } from './courseListEligibility';
import { Suspense as RouteSuspense } from 'react';
import { PageLoading as RouteLoading } from '@/app/Components/LoadingBubbles';
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from "react";
import { ScheduledCourse, GroupedCourse } from "@/app/types/course";
import { schedulesApi, usersApi, courseListsApi, socialApi, openSectionsService, snipeRequestsApi } from "@/app/services/api";
import { loadCatalogCourses as getParsedCourses } from "@/app/services/courseCatalogClient";
import { loadUserAcademicData } from "@/app/services/userAcademicData";
import type { FriendWithPrimarySchedule } from "@/app/services/api";
import { useAuth } from "@/app/Components/AuthProvider";
import { supabase } from "@/app/lib/supabase";
import "@/app/styles/resizable.css";
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { RutgersCourse } from "@/app/types/RutgersCourse";
import { normalizeBaseId } from "@/app/lib/courseIdFormatter";
import { XMarkIcon, ShareIcon, PlusIcon, AcademicCapIcon, ChevronDownIcon, PencilSquareIcon, ArrowPathIcon, CalendarDaysIcon, InformationCircleIcon, ArrowDownOnSquareIcon, Cog6ToothIcon, DocumentDuplicateIcon, Squares2X2Icon, ListBulletIcon, ChartBarIcon, LinkIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import { FunctionalIcon } from '@/app/Components/FunctionalIcon';
import ScheduleView from '@/app/Components/ScheduleView';
import ScheduleSelectionModal from '@/app/Components/ScheduleSelectionModal';
import WarningIndicator, { Warning } from '@/app/Components/WarningIndicator';
import Toast from '@/app/Components/Toast';
import OverlapResolutionModal from '@/app/Components/OverlapResolutionModal';
import { generateScheduleOptionsCooperatively, type CooperativeGenerationInput, type CooperativeGenerationResult } from '../services/scheduleGenerator';
import { useScheduleGeneration } from './useScheduleGeneration';
import { useScheduleSemesterChange } from './useScheduleSemesterChange';
import { useOwnedScheduleDirectory } from './useOwnedScheduleDirectory';
import { useAssistantScheduleRefresh } from './useAssistantScheduleRefresh';
import { useOwnedScheduleHydration, type HydrationTicket } from './useOwnedScheduleHydration';
import { getPhysicalWrite, SCHEDULE_WRITE_SCOPE } from '../hooks/physicalWriteSlot';
import OwnedHydrationRecovery from './components/OwnedHydrationRecovery';
import { useWebRegScheduleImport } from './useWebRegScheduleImport';
import { useScheduleCopy } from './useScheduleCopy';
import { useScheduleCopyPageState } from './useScheduleCopyPageState';
import { useBlankScheduleCreation, type BlankScheduleCreationReceipt } from './useBlankScheduleCreation';
import { commitScheduleSavePublication, isGenerationBlockedByScheduleWrite, useScheduleSaveOutcome } from './useScheduleSaveOutcome';
import ScheduleGenerationFeedback from './components/ScheduleGenerationFeedback';
import { Preferences as PreferencesType } from '../types/course';
import { SCHEDULE_DISPLAY_LIMITS } from '../lib/constants';
import { AVAILABILITY_DAYS, AVAILABILITY_SLOT_COUNT, getAvailabilitySlotIndex } from '../lib/availabilityGrid';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useGoogleCalendar } from '../hooks/useGoogleCalendar';
import { useAutoSave } from '@/app/hooks/useAutoSave';
import { useSelectedScheduleSave, type SelectedScheduleSaveIdentity } from './useSelectedScheduleSave';
import { useCourseRemovalIntent, useScheduleWriteAuthority, type ScheduleWriteOperation } from './useScheduleWriteAuthority';
import { createCourseAdditionHandler } from './courseAdditionHandler';
import { useCourseAdditionUndo } from './useCourseAdditionUndo';
import { classesAfterCourseRemoval, persistScheduleDraft, removalCourseId, removeScheduleCourse, shareScheduleCalendar } from './directScheduleWrites';
import { saveScheduleConfiguration, getConfigurationAutosaveRouteKey } from './saveScheduleConfiguration';
import { useMobileDetection } from '@/app/hooks/useMobileDetection';
import { useOnboardingModal } from '@/app/hooks/useOnboardingModal';
import { useCourseLists } from '@/app/hooks/useCourseLists';
import { useScheduleViewTimer } from '@/app/hooks/useScheduleViewTimer';
import { trackEvent, trackScheduleGenerated, trackCourseAdded, trackCourseRemoved, trackScheduleSaved } from '@/app/lib/posthog';
import AssistantButton from '@/app/Components/Assistant/AssistantButton';
import { useUiPreferences } from '@/app/Components/UiPreferencesProvider';
import type { AIMessage } from '@/app/types/ai';
import OnboardingModal from '@/app/Components/OnboardingModal';
import NoScheduleModal from '@/app/Components/NoScheduleModal';
import Preferences from '@/app/Components/Preferences';
import CourseConfigurationModal from '@/app/Components/CourseConfigurationModal';
import CourseListConfigurationModal from '@/app/Components/CourseListConfigurationModal';
import { getSafeRedirectPath } from '@/app/services/authHelpers';
import { selectedScheduleHref } from '@/app/lib/scheduleCourseHandoff';
import { isSemesterId, SEMESTERS } from '@/app/lib/semesters';
import {
  consumeOptimalSetupCalendarHandoff,
  isOptimalSetupCalendarTarget,
  recordVerifiedOptimalSetupUse,
} from '@/app/lib/optimalSetup';
import { resolveCoreJourneyReleaseFixture } from '@/app/lib/coreJourneyReleaseFixture';
import { CoreJourneyReleaseFixture } from '@/app/Components/CoreJourneyReleaseFixture';
import { PageLoading } from '@/app/Components/LoadingBubbles';
import { Button, IconButton } from '@/app/Components/Button';
import Modal from '@/app/Components/Modal';
import FinalsScheduleGrid from '@/app/Components/FinalsScheduleGrid';
import { Tooltip } from '@/app/Components/Tooltip';
import RotatingTips from '@/app/Components/RotatingTips';
import { friendOverlayColor } from '@/app/lib/friendOverlayColors';
import { useShellProfile } from '@/app/hooks/useShellProfile';
import FriendOverlayControl from './components/FriendOverlayControl';
import ScheduleCourseList from './components/ScheduleCourseList';
import ScheduleInsightsPanel from './components/ScheduleInsightsPanel';
import ScheduleGenerationsPanel from './components/ScheduleGenerationsPanel';
import ScheduleGettingStarted from './components/ScheduleGettingStarted';
import ScheduleHistoryControl from './components/ScheduleHistoryControl';
import SchedulingFeedbackFixture from './components/SchedulingFeedbackFixture';
import { TargetIcon, GoogleCalendarIcon } from '@/app/Components/AcademicIcons';
import { SegmentedControl, Tabs } from '@/app/Components/Tabs';
import { useAssistantPopup } from '@/app/Components/Assistant/AssistantContext';
import {
  applyScheduleChangeProposal,
  dismissScheduleChangeProposal,
  getPendingScheduleProposals,
  getScheduleRevisions,
  restoreScheduleRevision,
} from '@/app/services/academicVersioning';
import type { ScheduleChangeProposal, ScheduleRevision } from '@/app/types/academicChangeSet';
import type { SupportedSemester } from '@/app/services/courseCatalogShared';

const LeftPanel = dynamic(() => import('./components/LeftPanel'), { ssr: false });

import type { Schedule, GeneratedSchedule, CourseList } from '@/app/types/interfaces';

const EMPTY_CATALOG_SECTIONS: readonly { id: string; baseId: string }[] = [];

const DEBUG_SCHEDULING_UI = process.env.NEXT_PUBLIC_DEBUG_SCHEDULES === 'true';
const RECITATION_AUDIT_FIXTURE = process.env.NODE_ENV === 'development' ? 'recitation-meeting-types' : null;
const scheduleDebug = (...args: unknown[]) => {
  if (DEBUG_SCHEDULING_UI) {
    console.log(...args);
  }
};
const scheduleDebugGroupCollapsed = (...args: unknown[]) => {
  if (DEBUG_SCHEDULING_UI) {
    console.groupCollapsed(...args);
  }
};
const scheduleDebugGroupEnd = () => {
  if (DEBUG_SCHEDULING_UI) {
    console.groupEnd();
  }
};

// Configuration interface for storing left panel state
interface ScheduleConfiguration {
  selectedCourses: {
    id: string;
    name: string;
    selectedSectionId?: string; // Only store the selected section ID (DEPRECATED)
    isCourseList?: boolean;
    courseListId?: string; // Store the course list ID if it's a course list
    quantity?: number;
    allowedSections?: string[]; // NEW: Array of section IDs allowed for this course
    allowedCourses?: { [courseId: string]: string[] }; // NEW: For course lists: map of course IDs to their allowed sections
  }[];
  pinnedClasses: string[]; // DEPRECATED: kept for backward compatibility
  currentScheduleIndex: number;
  selectedCourseIds: string[]; // Array of course IDs that are currently selected/displayed
  customActivities?: CustomActivity[];
}

interface CustomActivity {
  id: string;
  title: string;
  day: string;
  startMinute: number;
  endMinute: number;
  campus: string;
}

type WorkspaceGenerationInput = CooperativeGenerationInput & { customActivityCourses: ScheduledCourse[]; openRevision: number };
interface GenerationIdentity { contextKey: string; signature: string; input: WorkspaceGenerationInput }

interface CustomActivityDraft {
  day: string;
  startMinute: number;
  endMinute: number;
  updatedAt: number;
}

// Helper function to get course data by class number
function getCourseDataByClassNumber(courses: RutgersCourse[], baseId: string): RutgersCourse[] {
  return courses.filter(course => course.baseId === baseId);
}

const normalizeCampusLabel = (value?: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();

  if (lower === 'nb') return 'NB';
  if (lower.includes('online')) return 'Online';
  if (lower.includes('remote')) return 'Remote';
  if (lower.includes('livingston')) return 'Livingston';
  if (lower.includes('busch')) return 'Busch';
  if (lower.includes('college avenue') || lower.includes('college ave')) return 'College Ave';
  if (lower.includes('cook') || lower.includes('douglass') || lower.includes('cook/douglass')) return 'Cook/Douglass';
  if (lower.includes('downtown') || lower.includes('new brunswick')) return 'Downtown New Brunswick';
  if (lower.includes('camden')) return 'Camden';
  if (lower.includes('newark')) return 'Newark';

  return trimmed;
};

const mapRutgersCourseToScheduledCourse = (course: RutgersCourse): ScheduledCourse => {
  const meetingTimes = (course.meetingTimes || []).map((mt) => {
    const normalizedCampus =
      normalizeCampusLabel(mt.campus) ??
      normalizeCampusLabel(course.campus) ??
      'NB';

    return {
      day: mt.day,
      startMinute: mt.startMinute,
      endMinute: mt.endMinute,
      location: mt.location,
      mode: mt.mode,
      campus: normalizedCampus,
      campusAbbrev: mt.campusAbbrev,
    };
  });

  const campusSet = new Set<string>();

  (course.campuses ?? []).forEach((value) => {
    const normalized = normalizeCampusLabel(value);
    if (normalized) campusSet.add(normalized);
  });

  const courseLevelCampus = normalizeCampusLabel(course.campus);
  if (courseLevelCampus) campusSet.add(courseLevelCampus);

  meetingTimes.forEach((mt) => {
    if (mt.campus) campusSet.add(mt.campus);
  });

  (course.meetingTimes || []).forEach((mt) => {
    const modeLower = mt.mode?.toLowerCase() ?? '';
    if (modeLower.includes('online')) {
      campusSet.add('Online');
    }
    if (modeLower.includes('remote')) {
      campusSet.add('Remote');
    }
  });

  if (courseLevelCampus && courseLevelCampus === 'Online') {
    campusSet.add('Online');
  }

  const campusList = Array.from(campusSet);
  if (campusList.length === 0) {
    campusList.push('NB');
  }

  const physicalCampuses = campusList.filter(
    (campus) => campus !== 'Online' && campus !== 'Remote'
  );
  const orderedCampusList = [
    ...physicalCampuses,
    ...campusList.filter((campus) => !physicalCampuses.includes(campus)),
  ];
  const primaryCampus = orderedCampusList[0] || 'NB';

  const firstMeeting = meetingTimes[0];
  const formattedTime = firstMeeting
    ? `${Math.floor(firstMeeting.startMinute / 60)}:${String(firstMeeting.startMinute % 60).padStart(2, '0')}`
    : 'TBA';

  return {
    id: course.id,
    baseId: course.baseId,
    name: course.courseName,
    campus: primaryCampus,
    campuses: orderedCampusList.length > 0 ? orderedCampusList : [primaryCampus],
    day: firstMeeting?.day || 'TBA',
    time: formattedTime,
    section: course.section,
    instructors: course.instructors,
    meetingTimes,
    class_number: course.class_number,
    department: course.department,
    location: firstMeeting?.location || 'TBA',
    credits: course.credits,
    coreRequirements: course.coreRequirements,
    examCode: course.examCode,
    finalExam: course.finalExam,
    isOpen: course.isOpen,
    baseSectionId: `${course.department}:${course.class_number}`,
  };
};

// --- Main Component ---
function SchedulingPage() {
  const searchParams = useSearchParams();
  if (process.env.NODE_ENV === 'development' && searchParams.get('auditFixture') === 'scheduling-feedback') return <SchedulingFeedbackFixture />;
  if (RECITATION_AUDIT_FIXTURE && searchParams.get('auditFixture') === RECITATION_AUDIT_FIXTURE) {
    const realAnalysis: ScheduledCourse = {
      id: '13403', baseId: '01640311', name: 'Introduction to Real Analysis I', campus: 'Busch', campuses: ['Busch'],
      day: 'Tuesday', time: '2:00 PM - 3:20 PM', section: '05', instructors: ['YUAN, YANGLIN'],
      meetingTimes: [
        { day: 'Tuesday', startMinute: 840, endMinute: 920, location: 'SEC 202', mode: 'LEC', campus: 'Busch' },
        { day: 'Wednesday', startMinute: 510, endMinute: 590, location: 'SEC 202', mode: 'RECIT', campus: 'Busch' },
        { day: 'Thursday', startMinute: 840, endMinute: 920, location: 'SEC 202', mode: 'LEC', campus: 'Busch' },
      ],
      class_number: '311', department: '640', credits: 3, isOpen: false,
    };
    return <div data-audit-ready="recitation-meeting-types" data-audit-owner="none" className="h-full min-h-screen bg-slate-950"><ScheduleView schedule={[realAnalysis]} onNextSchedule={() => undefined} onPrevSchedule={() => undefined} currentScheduleIndex={0} totalSchedules={1} totalSchedulesGenerated={1} semester="fall-2026" showAvailabilityOverlay={false} showDetailedCourseInformation={false} showCreditLoadStatus={false} sectionAvailabilityOverride={{ 13403: false }} /></div>;
  }
  const fixture = resolveCoreJourneyReleaseFixture('scheduling', searchParams.get('auditFixture'));
  return fixture ? <CoreJourneyReleaseFixture route="scheduling" /> : <SchedulingRouteRuntime />;
}

function SchedulingRouteRuntime() {
  const { user, loading: authLoading } = useAuth();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const idFromUrl = searchParams.get('id') || '';
  const routeRedirectTarget = useMemo(() => {
    const query = searchParams.toString();
    return getSafeRedirectPath(`${pathname}${query ? `?${query}` : ''}`);
  }, [pathname, searchParams]);

  useEffect(() => {
    if (authLoading || user) return;
    router.replace(
      idFromUrl
        ? `/scheduling/view?id=${idFromUrl}`
        : `/login?redirect=${encodeURIComponent(routeRedirectTarget)}`,
    );
  }, [user, authLoading, router, idFromUrl, routeRedirectTarget]);

  if (!mounted || authLoading) {
    return <PageLoading title="Preparing Scheduling" description="Loading your saved schedule, planner settings, and workspace context." />;
  }

  if (!user) {
    return (
      <PageLoading
        title={idFromUrl ? 'Opening public schedule view' : 'Sign in required'}
        description={idFromUrl
          ? 'Sending you to the public read-only schedule.'
          : 'Redirecting to login so your private scheduling workspace stays connected to your account.'}
      />
    );
  }

  return <SchedulingWorkspace key={user.id} />;
}

function SchedulingWorkspace() {
  const { activeSemester, setPreferredScheduleForSemester } = useUiPreferences();
  const { user, loading: authLoading } = useAuth();
  const comparisonProfile = useShellProfile(user?.id);
  const { openAssistant } = useAssistantPopup();
  const router = useRouter();
  const searchParams = useSearchParams();
  const scheduleId = searchParams.get('id');
  const creationPathname = usePathname();
  const scheduleRouteKey = JSON.stringify([creationPathname, searchParams.toString()]);
  const configurationSaveRouteKey = getConfigurationAutosaveRouteKey(creationPathname, searchParams.toString());
  const [optimalSetupCalendarHandoff, setOptimalSetupCalendarHandoff] = useState<{
    ownerUserId: string;
    entrypoint: 'onboarding' | 'settings';
  } | null>(null);
  const optimalSetupCalendarVerifiedRef = useRef(false);
  useEffect(() => {
    if (!user?.id || typeof window === 'undefined') {
      setOptimalSetupCalendarHandoff(null);
      optimalSetupCalendarVerifiedRef.current = false;
      return;
    }
    if (optimalSetupCalendarHandoff?.ownerUserId === user.id) return;
    optimalSetupCalendarVerifiedRef.current = false;
    const entrypoint = consumeOptimalSetupCalendarHandoff(window.sessionStorage, user.id, searchParams);
    setOptimalSetupCalendarHandoff(entrypoint ? { ownerUserId: user.id, entrypoint } : null);
  }, [optimalSetupCalendarHandoff?.ownerUserId, searchParams, user?.id]);
  const isMobile = useMobileDetection();
  const { showModal, closeModal } = useOnboardingModal();

  // Use the centralized Supabase client from app/lib/supabase.ts
  // No need to create a new client instance
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [activeScheduleProposal, setActiveScheduleProposal] = useState<ScheduleChangeProposal | null>(null);
  const [scheduleRevisions, setScheduleRevisions] = useState<ScheduleRevision[]>([]);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [proposalApplying, setProposalApplying] = useState(false);

  // Onboarding Modal - will be rendered at the end of the component
  const [pinnedClasses, setPinnedClasses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourses, setSelectedCourses] = useState<GroupedCourse[]>([]);
  const [selectedCoursesList, setSelectedCoursesList] = useState<CourseList[]>([]);
  const [customActivities, setCustomActivities] = useState<CustomActivity[]>([]);
  const [customActivityDraft, setCustomActivityDraft] = useState<CustomActivityDraft | null>(null);
  const [availabilityOverlayEnabled, setAvailabilityOverlayEnabled] = useState(true);
  const [completedCourseBaseIds, setCompletedCourseBaseIds] = useState<Set<string>>(new Set());
  const [completedCoursesOwner, setCompletedCoursesOwner] = useState<string | null>(null);
  const [completedCoursesLoadError, setCompletedCoursesLoadError] = useState<string | null>(null);
  const [shouldForceRegenerate, setShouldForceRegenerate] = useState(false);
  const [shouldRegenerateAfterConfigClose, setShouldRegenerateAfterConfigClose] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const ownDirectSaveOperation = useRef<ScheduleWriteOperation | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);


  // Schedule generation state
  const [generatedSchedules, setGeneratedSchedules] = useState<GeneratedSchedule[]>([]);
  const [allScoredSchedules, setAllScoredSchedules] = useState<GeneratedSchedule[]>([]);
  const [currentScheduleIndex, setCurrentScheduleIndex] = useState(-1);
  const [totalSchedulesGenerated, setTotalSchedulesGenerated] = useState<number>(0);
  const [isViewingSelectedSchedule, setIsViewingSelectedSchedule] = useState(true);
  const [workspaceTab, setWorkspaceTab] = useState<'schedule' | 'information' | 'generations'>('schedule');
  const [schedulePresentation, setSchedulePresentation] = useState<'grid' | 'list' | 'finals'>('grid');
  const [focusedInsightCourseId, setFocusedInsightCourseId] = useState<string | null>(null);
  const [showMobileAdditionalOptions, setShowMobileAdditionalOptions] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info'; isVisible: boolean }>({
    message: '',
    type: 'info',
    isVisible: false
  });

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    setToast({ message, type, isVisible: true });
  };
  const [generationIdentity, setGenerationIdentity] = useState<GenerationIdentity | null>(null);
  const [openSectionsRevision, setOpenSectionsRevision] = useState(0);
  const [showRegenerationIndicator, setShowRegenerationIndicator] = useState(false);

  // Maximum number of schedules to display (performance optimization)
  const MAX_SCHEDULES_TO_DISPLAY = SCHEDULE_DISPLAY_LIMITS.MAX_SCHEDULES_TO_DISPLAY;

  // Warning system state
  const [warnings, setWarnings] = useState<Warning[]>([]);

  // All course data
  const [allCourses, setAllCourses] = useState<RutgersCourse[]>([]);
  const [loadedCatalogTerm, setLoadedCatalogTerm] = useState<string | null>(null);
  const initializedScheduleKey = useRef<string | null>(null);
  const [hasReconstructedSections, setHasReconstructedSections] = useState(false);
  const [hasReconstructedCourseLists, setHasReconstructedCourseLists] = useState(false);

  // Course lists data

  // myCourseLists is now managed by the useCourseLists hook
  const [friendsWithSchedules, setFriendsWithSchedules] = useState<FriendWithPrimarySchedule[]>([]);
  const [friendPresenceBySection, setFriendPresenceBySection] = useState<Record<string, { avatarUrl?: string; displayName?: string; userId: string }[]>>({});
  const [friendsSameCourseBaseIds, setFriendsSameCourseBaseIds] = useState<Set<string>>(new Set());
  const [selectedFriendIdsForOverlay, setSelectedFriendIdsForOverlay] = useState<string[]>([]);
  const [showOwnScheduleForOverlay, setShowOwnScheduleForOverlay] = useState(true);
  const [showFriendOverlayDropdown, setShowFriendOverlayDropdown] = useState(false);
  const [showSnipeDialog, setShowSnipeDialog] = useState(false);
  const [selectedSnipeIndexes, setSelectedSnipeIndexes] = useState<string[]>([]);
  const [sniping, setSniping] = useState(false);

  // Preferences state
  const [preferences, setPreferences] = useState<PreferencesType | null>(null);
  const [preferencesOwner, setPreferencesOwner] = useState<string | null>(null);
  const [preferencesLoadError, setPreferencesLoadError] = useState<string | null>(null);
  const [generationInputRetry, setGenerationInputRetry] = useState(0);
  const [openSectionsLoadingTerm, setOpenSectionsLoadingTerm] = useState<string | null>(null);
  const [openSectionsLoadError, setOpenSectionsLoadError] = useState<string | null>(null);

  // Schedule selection modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isModalOpenedManually, setIsModalOpenedManually] = useState(false);
  const chooserDismissed = useRef(false);
  useLayoutEffect(() => { chooserDismissed.current = false; }, [user?.id, scheduleRouteKey]);
  const readOwnedSchedules = useCallback((owner: string): Promise<React.ComponentProps<typeof ScheduleSelectionModal>['schedules']> => schedulesApi.get_all_user_schedules(owner), []);
  const readOwnedProfile = useCallback(async (owner: string) => {
    // The legacy cached get() may persist default preferences while reading.
    // This boundary must stay read-only and verify the requested owner.
    const response = await usersApi.getFresh(owner);
    const profile: unknown = Array.isArray(response) ? response.length === 1 ? response[0] : null : response;
    if (!profile || typeof profile !== 'object' || !('uid' in profile) || profile.uid !== owner) {
      throw new Error('Your profile could not be loaded.');
    }
    return profile;
  }, []);
  // Own the read before any await; consumed add-command cleanup is the existing
  // configuration route exception, while every unrelated query/path retires it.
  const hydration = useOwnedScheduleHydration({
    ownerId: user?.id ?? null, scheduleId, routeKey: configurationSaveRouteKey,
    visibleTerm: schedule?.term_id || activeSemester, enabled: !authLoading,
    hasWorkspace: Boolean(schedule?.user_id === user?.id && schedule?.schedule_id === scheduleId
      && schedule?.term_id && loadedCatalogTerm === schedule.term_id
      && initializedScheduleKey.current === `${schedule.schedule_id}:${schedule.term_id}`),
    readSchedule: id => schedulesApi.get(id), readProfile: readOwnedProfile,
    readCatalog: getParsedCourses, mapSection: mapRutgersCourseToScheduledCourse,
    onLoading: (loading, isCurrent) => setIsLoading(previous => isCurrent() ? loading : previous),
    onForeign: (record, isCurrent) => {
      if (!isCurrent()) return;
      if (record.is_public) router.push(`/scheduling/view?id=${record.schedule_id}`);
      // Private denial is surfaced by the receipt-owned error/recovery state.
    },
    publish: ({ record, catalog, projection }, isCurrent) => {
      // All setters and readiness belong to the same immediate accepted commit.
      if (!isCurrent()) return;
      setSchedule(previous => isCurrent() ? record : previous);
      setAllCourses(previous => isCurrent() ? catalog : previous);
      setLoadedCatalogTerm(previous => isCurrent() ? record.term_id! : previous);
      setTempName(previous => isCurrent() ? record.name || '' : previous);
      setSelectedCourses(previous => isCurrent() ? projection.selectedCourses : previous);
      setPinnedClasses(previous => isCurrent() ? projection.pinnedClasses : previous);
      setCustomActivities(previous => isCurrent() ? projection.customActivities : previous);
      setIsViewingSelectedSchedule(previous => isCurrent() ? true : previous);
      setCurrentScheduleIndex(previous => isCurrent() ? -1 : previous);
      setHasReconstructedSections(previous => isCurrent() ? false : previous);
      setHasReconstructedCourseLists(previous => isCurrent() ? false : previous);
      setHasUnsavedChanges(previous => isCurrent() ? false : previous);
      setError(previous => isCurrent() ? null : previous);
      if (!isModalOpenedManually) setShowScheduleModal(previous => isCurrent() ? false : previous);
      if (isCurrent()) initializedScheduleKey.current = `${record.schedule_id}:${record.term_id}`;
    },
  });
  const readOwnedPrimaryId = useCallback(async (owner: string): Promise<string | null> => {
    const profile = await readOwnedProfile(owner);
    if (!('primary_schedule_id' in profile)) throw new Error('Your primary schedule could not be loaded.');
    const primary = profile.primary_schedule_id;
    if (primary === null) return null;
    if (typeof primary !== 'string') throw new Error('Your primary schedule could not be loaded.');
    return primary || null;
  }, [readOwnedProfile]);
  const { schedules: userSchedules, primaryScheduleId, setPrimaryScheduleId,
    refresh: fetchUserSchedules, isLoading: schedulesDirectoryLoading,
    error: schedulesDirectoryError } = useOwnedScheduleDirectory({
      ownerId: user?.id ?? null, routeKey: scheduleRouteKey,
      readSchedules: readOwnedSchedules, readPrimaryId: readOwnedPrimaryId,
    });

  // Open sections toggle state
  const [generateOnlyOpenSections, setGenerateOnlyOpenSections] = useState(false);

  // Overlap resolution state
  const [showOverlapModal, setShowOverlapModal] = useState(false);
  const [overlapConflicts, setOverlapConflicts] = useState<Array<{
    course1: ScheduledCourse;
    course2: ScheduledCourse;
    day: string;
    timeConflict: string;
  }>>([]);

  // No schedule modal state
  const [showNoScheduleModal, setShowNoScheduleModal] = useState(false);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);

  // Save prompt state - moved from ScheduleView
  const [showSavePrompt, setShowSavePrompt] = useState(false);

  // Course configuration modal state
  const [courseConfigState, setCourseConfigState] = useState<{
    courseId: string | null;
    focusSectionId?: string;
  }>({ courseId: null });
  const [courseListConfigState, setCourseListConfigState] = useState<{
    groupId: string | null;
    initialCourseId?: string;
    initialSectionId?: string;
  }>({ groupId: null });

  useEffect(() => {
    if (!courseConfigState.courseId) return;
    const stillExists = selectedCourses.some(
      (course) => !course.isCourseList && course.id === courseConfigState.courseId
    );
    if (!stillExists) {
      setCourseConfigState({ courseId: null });
    }
  }, [courseConfigState.courseId, selectedCourses]);

  useEffect(() => {
    if (!courseListConfigState.groupId) return;
    const stillExists = selectedCourses.some(
      (course) => course.isCourseList && course.id === courseListConfigState.groupId
    );
    if (!stillExists) {
      setCourseListConfigState({ groupId: null });
    }
  }, [courseListConfigState.groupId, selectedCourses]);

  const activeCourseForConfig = useMemo(() => {
    if (!courseConfigState.courseId) return null;
    return selectedCourses.find(
      (course) => !course.isCourseList && course.id === courseConfigState.courseId
    ) || null;
  }, [courseConfigState.courseId, selectedCourses]);

  const activeCourseListForConfig = useMemo(() => {
    if (!courseListConfigState.groupId) return null;
    return selectedCourses.find(
      (course) => course.isCourseList && course.id === courseListConfigState.groupId
    ) || null;
  }, [courseListConfigState.groupId, selectedCourses]);

  const handoffTerm = searchParams.get('semester');
  // Saved schedules own their term; otherwise honor an explicit term handoff.
  const catalogTerm = schedule?.term_id || (isSemesterId(handoffTerm) ? handoffTerm : activeSemester);
  const openSectionsTermId = catalogTerm;
  const generationContextKey = JSON.stringify([user?.id ?? null, schedule?.schedule_id ?? null, scheduleId ?? null]);
  const generationBelongsToWorkspace = generationIdentity?.contextKey === generationContextKey;
  const generatedTerm = generationBelongsToWorkspace ? generationIdentity.input.openSectionsTermId : catalogTerm;
  const displayedTerm = isViewingSelectedSchedule ? catalogTerm : generatedTerm;
  useLayoutEffect(() => {
    setGeneratedSchedules([]);
    setAllScoredSchedules([]);
    setTotalSchedulesGenerated(0);
    setGenerationIdentity(null);
    setCurrentScheduleIndex(-1);
    setIsViewingSelectedSchedule(true);
    setShowNoScheduleModal(false);
  }, [generationContextKey]);

  const courseListGenerationNotice = useMemo(() => {
    const limit = SCHEDULE_DISPLAY_LIMITS.MAX_DISTINCT_COURSE_LIST_COURSES;
    const limitedLists = selectedCourses
      .filter(course => course.isCourseList)
      .map(course => {
        const distinctCourses = new Set((course.sections || []).map(section => section.baseId)).size;
        return {
          name: course.name,
          distinctCourses,
        };
      })
      .filter(course => course.distinctCourses > limit);

    if (limitedLists.length === 0) return null;

    const listNames = limitedLists
      .slice(0, 2)
      .map(course => `${course.name} (${course.distinctCourses})`)
      .join(', ');
    const suffix = limitedLists.length > 2 ? ` and ${limitedLists.length - 2} more` : '';
    return `Course-list generation explores the first ${limit} distinct courses per list. Limited: ${listNames}${suffix}.`;
  }, [selectedCourses]);

  const isConfigModalOpen = courseConfigState.courseId !== null || courseListConfigState.groupId !== null;
  // Timer hook for showing save prompt after 5 seconds when viewing generated schedules
  // Only run timer when viewing generated schedules (not selected schedule)
  const shouldShowPrompt = !isViewingSelectedSchedule && generatedSchedules.length > 0;
  const { hasShownPrompt } = useScheduleViewTimer({
    scheduleIndex: shouldShowPrompt ? currentScheduleIndex : -999, // Use -999 to prevent timer when not viewing generated schedules
    onShowSavePrompt: () => {
      if (shouldShowPrompt) {
        setShowSavePrompt(true);
      }
    },
    thresholdMs: 5000
  });

  // Hide the prompt after a few seconds or when switching schedules
  useEffect(() => {
    if (showSavePrompt) {
      const timer = setTimeout(() => {
        setShowSavePrompt(false);
      }, 3000); // Hide after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [showSavePrompt]);

  // Reset prompt when switching between selected and generated schedules
  useEffect(() => {
    setShowSavePrompt(false);
  }, [isViewingSelectedSchedule]);

  // Temporary mock flag to preview friends overlays without API
  const MOCK_FRIENDS = false;

  // Mobile tab state


  const customActivityCourses = useMemo<ScheduledCourse[]>(() => {
    return customActivities.map((activity) => ({
      id: `custom:${activity.id}`,
      baseId: `custom:${activity.id}`,
      name: activity.title || 'Custom Activity',
      campus: activity.campus || 'Unknown',
      campuses: activity.campus ? [activity.campus] : ['Unknown'],
      day: activity.day,
      time: '',
      section: 'Activity',
      instructors: ['Custom'],
      meetingTimes: [{
        day: activity.day,
        startMinute: activity.startMinute,
        endMinute: activity.endMinute,
        location: activity.campus || 'TBA',
        mode: 'custom',
        campus: activity.campus || 'Unknown',
      }],
      class_number: 'ACT',
      department: 'CUSTOM',
      location: activity.campus || 'TBA',
      credits: 0,
    }));
  }, [customActivities]);

  // Google Calendar export functionality
  const { exportToGoogleCalendar, isExporting, lastExportResult } = useGoogleCalendar();

  // Export dropdown state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showMobilePreferences, setShowMobilePreferences] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.export-dropdown')) {
        setShowExportDropdown(false);
      }
      if (!target.closest('.friend-overlay-dropdown')) {
        setShowFriendOverlayDropdown(false);
      }
    };

    if (showExportDropdown || showFriendOverlayDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportDropdown, showFriendOverlayDropdown]);

  // Update page title based on schedule name
  useEffect(() => {
    if (schedule?.name) {
      document.title = `${schedule.name} - Schedule Builder - Scarlet Sync`;
    } else {
      document.title = 'Schedule Builder - Scarlet Sync';
    }
  }, [schedule?.name]);

  // Debug modal state
  useEffect(() => {
    scheduleDebugGroupCollapsed('Scheduling: Modal State');
    scheduleDebug('Changed:', showScheduleModal);
    scheduleDebugGroupEnd();
    void fetchUserSchedules().catch(() => { /* The current directory owns its read error. */ });
  }, [showScheduleModal, user?.id, scheduleRouteKey, fetchUserSchedules]);

  // Opening the chooser is synchronous; the owner-bound loader separately
  // supplies its loading/error state rather than reopening it after a late read.
  useEffect(() => {
    if (user?.id && !authLoading && !isLoading && !schedule && !showScheduleModal && !chooserDismissed.current) {
      setShowScheduleModal(true);
      setIsModalOpenedManually(false);
    }
  }, [user?.id, authLoading, isLoading, schedule, showScheduleModal]);

  const handlePreferencesChange = async (newPrefs: PreferencesType) => {
    setPreferences(newPrefs);
    if (user?.id) {
      try {
        await usersApi.update({ p_preferences_json: newPrefs });
      } catch (error) {
        console.error('Failed to save preferences:', error);
      }
    }
  };

  const semesterHydrationTicket = useRef<HydrationTicket | null>(null);
  const commitSemesterChange = useCallback(() => {
    const ticket = semesterHydrationTicket.current; semesterHydrationTicket.current = null;
    if (ticket) void hydration.resumeTerm(ticket);
  }, [hydration.resumeTerm]);
  const writeSemester = useCallback(async (id: string, term: string) => {
    const ticket = hydration.reserveTerm(term);
    if (!ticket) throw new Error('Loading saved changes. Saving is paused.');
    semesterHydrationTicket.current = ticket;
    try { return await schedulesApi.update(id, { term_id: term }); }
    catch (error) {
      hydration.rejectTerm(ticket);
      if (semesterHydrationTicket.current === ticket) semesterHydrationTicket.current = null;
      throw error;
    }
  }, [hydration.reserveTerm, hydration.rejectTerm]);
  const semesterChange = useScheduleSemesterChange({
    ownerId: user?.id ?? null,
    scheduleId: schedule?.user_id === user?.id && schedule?.schedule_id === scheduleId ? scheduleId : null,
    routeKey: scheduleRouteKey, currentTerm: catalogTerm,
    writeTerm: writeSemester, onCommit: commitSemesterChange,
  });
  const handleSemesterChange = useCallback((term: string) => hydration.canWrite()
    ? semesterChange.changeTerm(term) : Promise.resolve(false), [hydration.canWrite, semesterChange.changeTerm]);

  // Share the existing list loader rather than requesting official lists twice.
  const { courseLists: myCourseLists, officialCourseLists: courseLists,
    isLoading: courseListsLoading, error: courseListsLoadError, loadCourseLists } = useCourseLists();

  // Debug course lists loading
  useEffect(() => {
    scheduleDebug('Course lists state:', {
      courseLists: courseLists.length,
      myCourseLists: myCourseLists.length,
      courseListsData: courseLists.map(cl => ({ id: cl.id, name: cl.name }))
    });
  }, [courseLists, myCourseLists]);

  const creationQuery = searchParams.toString();
  const requestedCreationTerm = SEMESTERS.find(semester => semester.id === searchParams.get('semester'))?.id ?? activeSemester;
  const creationContextKey = JSON.stringify([user?.id ?? null, requestedCreationTerm, creationPathname, creationQuery]);
  const chooserCreationTermLabel = SEMESTERS.find((semester) => semester.id === requestedCreationTerm)?.label || 'New';
  const chooserCreation = useBlankScheduleCreation({
    mode: 'chooser', ownerId: user?.id ?? null, scheduleId,
    routeKey: scheduleRouteKey, term: requestedCreationTerm, name: `My ${chooserCreationTermLabel} Schedule`,
    enabled: showScheduleModal && !authLoading && !schedulesDirectoryLoading && !schedulesDirectoryError,
    primaryScheduleId,
    isRouteCurrent: () => typeof window === 'undefined' || (window.location.pathname === creationPathname
      && new URLSearchParams(window.location.search).toString() === creationQuery),
    createSchedule: payload => schedulesApi.create(payload),
    assignPrimary: id => usersApi.update({ p_primary_schedule_id: id }),
    navigate: id => { setIsModalOpenedManually(false); router.push(selectedScheduleHref(id, creationQuery)); },
  });

  // A chooser refusal must remain false so the modal does not close.
  const handleModalScheduleSelect = async (selectedId: string, review?: BlankScheduleCreationReceipt): Promise<void | false> => {
    if (selectedId === 'new') {
      if (!review) return false;
      return await chooserCreation.createFromChooser(review) ? undefined : false;
    }
    chooserCreation.retireChooser();
    setIsModalOpenedManually(false);
    router.push(selectedScheduleHref(selectedId, searchParams.toString()));
  };

  // Imported courses retain the existing term, public visibility and configuration.
  const webRegImportDraft = useMemo(() => ({ allCourses, customActivities }), [allCourses, customActivities]);
  const { importSchedule: handleWebRegImport } = useWebRegScheduleImport({
    ownerId: user?.id ?? null, routeKey: scheduleRouteKey,
    term: catalogTerm, isOpen: showScheduleModal && !authLoading,
    draft: webRegImportDraft, catalog: allCourses, customActivities, primaryScheduleId,
    isRouteCurrent: () => typeof window === 'undefined' || (window.location.pathname === creationPathname
      && new URLSearchParams(window.location.search).toString() === creationQuery),
    createSchedule: payload => schedulesApi.create(payload),
    assignPrimary: id => usersApi.update({ p_primary_schedule_id: id }),
    refreshDirectory: fetchUserSchedules,
    navigate: id => router.push(`/scheduling?id=${id}`),
  });

  const assistantContext = useMemo(() => {
    const currentScheduleId = schedule?.schedule_id ?? scheduleId ?? undefined;
    const context: Record<string, unknown> = {
      // Activate the specialized scheduling agent
      skill: 'scheduling',
    };

    if (currentScheduleId) {
      context.scheduleId = currentScheduleId;
    }

    if (schedule) {
      context.scheduleName = schedule.name || 'Unnamed Schedule';
      context.termId = schedule.term_id || activeSemester;
      context.isPublic = schedule.is_public || false;
    }

    // Compute current schedule courses (either from saved schedule or generated schedules)
    const currentScheduleCourses: ScheduledCourse[] = isViewingSelectedSchedule && schedule?.classes
      ? schedule.classes.map(classId => {
        const [baseId, sectionId] = classId.split(':');
        const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
        return course ? mapRutgersCourseToScheduledCourse(course) : null;
      }).filter(Boolean) as ScheduledCourse[]
      : generationBelongsToWorkspace ? generatedSchedules[currentScheduleIndex]?.courses || [] : [];

    // Add current schedule courses info
    if (currentScheduleCourses && currentScheduleCourses.length > 0) {
      context.currentCourses = currentScheduleCourses.map(course => ({
        id: course.id,
        baseId: course.baseId,
        name: course.name,
        department: course.department,
        classNumber: course.class_number,
        section: course.section,
        credits: course.credits,
        campuses: course.campuses || [course.campus],
        meetingTimes: course.meetingTimes?.map(mt => ({
          day: mt.day,
          startMinute: mt.startMinute,
          endMinute: mt.endMinute,
          location: mt.location,
          campus: mt.campus,
        })) || [],
      }));
      context.totalCredits = currentScheduleCourses.reduce((sum, c) => {
        const creditValue = typeof c.credits === 'number' ? c.credits : Number(c.credits ?? 0);
        return sum + (Number.isFinite(creditValue) ? creditValue : 0);
      }, 0);
    }

    // Add selected courses info
    if (selectedCourses && selectedCourses.length > 0) {
      context.selectedCourses = selectedCourses.map(course => {
        const section = course.selectedSection || (course.sections && course.sections[0]) || undefined;
        return {
          id: course.id,
          name: course.name,
          baseId: section?.baseId,
          department: section?.department,
          classNumber: section?.class_number,
        };
      });
    }

    if (preferences) {
      context.preferences = {
        preferredCampuses: preferences.preferredCampuses,
        earliestStartTime: preferences.earliestStartTime,
        latestEndTime: preferences.latestEndTime,
        minimumProfessorRating: preferences.minimumProfessorRating,
        disableLogisticsPenalties: preferences.disableLogisticsPenalties,
        priorities: preferences.priorities,
        campusPreferences: preferences.campusPreferences,
      };
    }

    // What the builder is showing right now, as facts the assistant can quote back.
    // Deliberately labelled prose rather than internal state: the model reads the
    // screen, it does not drive it.
    context.visibleView = [
      { label: 'View', value: isViewingSelectedSchedule ? 'Saved schedule' : 'Generated options' },
      ...(isViewingSelectedSchedule
        ? []
        : generatedSchedules.length
          ? [{ label: 'Showing option', value: `${currentScheduleIndex + 1} of ${generatedSchedules.length}` }]
          : [{ label: 'Showing option', value: 'None generated yet' }]),
      { label: 'Courses on screen', value: String(currentScheduleCourses.length) },
      ...(typeof context.totalCredits === 'number' ? [{ label: 'Credits on screen', value: String(context.totalCredits) }] : []),
      ...(selectedCourses?.length ? [{ label: 'Courses in the cart', value: String(selectedCourses.length) }] : []),
      ...(preferences?.preferredCampuses?.length ? [{ label: 'Campus filter', value: preferences.preferredCampuses.join(', ') }] : []),
    ];

    return context;
  }, [schedule?.schedule_id, scheduleId, schedule, isViewingSelectedSchedule, schedule?.classes, generatedSchedules, currentScheduleIndex, allCourses, selectedCourses, preferences]);

  const overlayFriendSchedules = useMemo(() => {
    if (!friendsWithSchedules || friendsWithSchedules.length === 0 || selectedFriendIdsForOverlay.length === 0) return undefined;

    return selectedFriendIdsForOverlay.map(friendId => {
      const friendData = friendsWithSchedules.find(f => f.friend.id === friendId);
      if (!friendData || !friendData.primarySchedule?.classes) return null;

      const courses = friendData.primarySchedule.classes.map(classId => {
        const [baseId, sectionId] = classId.split(':');
        const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
        return course ? mapRutgersCourseToScheduledCourse(course) : null;
      }).filter(Boolean) as ScheduledCourse[];

      return {
        friendName: friendData.friend.name || friendData.friend.display_name || 'Friend',
        color: friendOverlayColor(friendsWithSchedules.filter(friend => (friend.primarySchedule?.classes?.length ?? 0) > 0).findIndex(friend => friend.friend.id === friendId)),
        courses
      };
    }).filter(Boolean) as { friendName: string, color: string, courses: ScheduledCourse[] }[];
  }, [friendsWithSchedules, selectedFriendIdsForOverlay, allCourses]);

  const assistantInitialMessages = useMemo<AIMessage[]>(() => {
    if (schedule?.schedule_id) {
      const scheduleLabel = schedule?.name ? `"${schedule.name}"` : 'your current schedule';
      return [
        {
          role: 'assistant',
          content: [
            {
              type: 'text',
              text: `Hi! I'm ready to help you work on ${scheduleLabel}. Ask me to search for courses, add classes, or remove anything you don't need.`,
            },
          ],
        },
      ];
    }

    return [
      {
        role: 'assistant',
        content: [
          {
            type: 'text',
            text: "Hi! I'm your AI scheduling assistant. Let me know what you'd like to change or explore in your schedule.",
          },
        ],
      },
    ];
  }, [schedule?.schedule_id, schedule?.name]);

  const assistantSuggestions = useMemo(() => {
    if (!schedule?.schedule_id) {
      return [
        'Help me build a schedule with morning classes only.',
        'Find open CS electives that fit on Tuesdays and Thursdays.',
        'Check for conflicts if I add Calculus and Physics.',
      ];
    }

    return [
      'Remove the chemistry class and add an upper-level CS elective.',
      'Suggest replacements for my Friday afternoon classes.',
      'Check if any of my courses conflict or exceed 17 credits.',
      'Find lab sections that fit after 3 PM.',
    ];
  }, [schedule?.schedule_id]);

  const addParamFromUrl = searchParams.get('add') || '';
  const handledCourseHandoff = useRef<string | null>(null);

  const isExportMode = isOptimalSetupCalendarTarget(searchParams);

  useEffect(() => {
    if (scheduleId) return; // Owned catalog is buffered by hydration, never a fresh read effect.
    const semester = catalogTerm;
    let cancelled = false;
    setLoadedCatalogTerm(null);
    initializedScheduleKey.current = null;
    void getParsedCourses(semester).then(courses => {
      if (cancelled) return;
      setAllCourses(courses);
      setLoadedCatalogTerm(semester);
    }).catch(() => {
      if (!cancelled) { setAllCourses([]); setLoadedCatalogTerm(null); }
    });
    return () => { cancelled = true; };
  }, [catalogTerm, scheduleId]);

  // Filter schedules for current semester (for chips display)
  const schedulesForCurrentSemester = useMemo(() => {
    if (!schedule?.term_id || !userSchedules.length) return [];
    return userSchedules.filter((s: any) =>
      s.term_id === schedule.term_id &&
      s.schedule_id !== schedule?.schedule_id
    );
  }, [userSchedules, schedule?.term_id, schedule?.schedule_id]);

  const selectedCourseCount = useMemo(() => {
    return selectedCourses.reduce((total, course) => {
      if (course.isCourseList) {
        return total + (course.quantity || 1);
      }
      return total + 1;
    }, 0);
  }, [selectedCourses]);

  const displayedScheduleCourses = useMemo(() => {
    const baseCourses = isViewingSelectedSchedule && schedule?.classes
      ? schedule.classes.map(classId => {
        const [baseId, sectionId] = classId.split(':');
        const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
        return course ? mapRutgersCourseToScheduledCourse(course) : null;
      }).filter(Boolean) as ScheduledCourse[]
      : generatedSchedules[currentScheduleIndex]?.courses || [];

    const activities = !isViewingSelectedSchedule && generationBelongsToWorkspace
      ? generationIdentity.input.customActivityCourses : customActivityCourses;
    return [...baseCourses, ...activities];
  }, [isViewingSelectedSchedule, schedule?.classes, generatedSchedules, currentScheduleIndex, allCourses, generationBelongsToWorkspace, generationIdentity, customActivityCourses]);

  const closedDisplayedCourses = useMemo(() => displayedScheduleCourses.filter((course) => {
    if (course.id.startsWith('custom:')) return false;
    const sectionIndex = course.id.split(':').pop() || '';
    return Boolean(sectionIndex) && !openSectionsService.isOpen(sectionIndex, displayedTerm);
  }), [displayedScheduleCourses, displayedTerm]);

  const openSnipePicker = useCallback(() => {
    setSelectedSnipeIndexes(closedDisplayedCourses.map((course) => course.id.split(':').pop() || '').filter(Boolean));
    setShowSnipeDialog(true);
  }, [closedDisplayedCourses]);

  const addSelectedScheduleSnipes = useCallback(async () => {
    if (!user?.id || !selectedSnipeIndexes.length) return;
    setSniping(true);
    try {
      const termId = (schedule?.term_id || activeSemester) as SupportedSemester;
      await Promise.all(selectedSnipeIndexes.map((index) => {
        const course = closedDisplayedCourses.find((entry) => entry.id.split(':').pop() === index);
        return snipeRequestsApi.create(user.id, index, termId, course ? `${course.baseId} · ${course.name} · Section ${course.section}` : null);
      }));
      setShowSnipeDialog(false);
      showToast(`${selectedSnipeIndexes.length} closed section${selectedSnipeIndexes.length === 1 ? '' : 's'} added to Course Sniper.`, 'success');
    } catch (cause) {
      showToast(cause instanceof Error ? cause.message : 'Could not add the selected sections to Course Sniper.', 'error');
    } finally {
      setSniping(false);
    }
  }, [activeSemester, closedDisplayedCourses, schedule?.term_id, selectedSnipeIndexes, user?.id]);

  const proposalSchedulePreview = useMemo(() => {
    if (!activeScheduleProposal || !isViewingSelectedSchedule || schedule?.user_id !== userId) {
      return { courses: displayedScheduleCourses, changeStates: {} as Record<string, 'added' | 'removed'> };
    }

    const currentAcademicCourses = displayedScheduleCourses.filter((course) => !course.id.startsWith('custom:') && course.department !== 'CUSTOM');
    const customCourses = displayedScheduleCourses.filter((course) => course.id.startsWith('custom:') || course.department === 'CUSTOM');
    const resolveSection = (identifier: string) => {
      const normalizedIdentifier = identifier.replace(/:/g, '');
      const course = allCourses.find((candidate) => {
        const fullId = `${normalizeBaseId(candidate.baseId)}:${candidate.id}`;
        return candidate.id === identifier
          || candidate.class_number === identifier
          || fullId === identifier
          || `${candidate.baseId}${candidate.id}` === normalizedIdentifier;
      });
      return course ? mapRutgersCourseToScheduledCourse(course) : null;
    };
    const proposedCourses = activeScheduleProposal.preview_snapshot.classes
      .map(resolveSection)
      .filter(Boolean) as ScheduledCourse[];
    const currentIds = new Set(currentAcademicCourses.map((course) => course.id));
    const proposedIds = new Set(proposedCourses.map((course) => course.id));
    const removedCourses = currentAcademicCourses.filter((course) => !proposedIds.has(course.id));
    const changeStates: Record<string, 'added' | 'removed'> = {};
    proposedCourses.forEach((course) => {
      if (!currentIds.has(course.id)) changeStates[course.id] = 'added';
    });
    removedCourses.forEach((course) => { changeStates[course.id] = 'removed'; });

    return { courses: [...proposedCourses, ...removedCourses, ...customCourses], changeStates };
  }, [activeScheduleProposal, allCourses, displayedScheduleCourses, isViewingSelectedSchedule, schedule?.user_id, userId]);

  const refreshScheduleVersioning = useCallback(async () => {
    if (!schedule?.schedule_id || !userId || schedule.user_id !== userId) {
      setActiveScheduleProposal(null);
      setScheduleRevisions([]);
      return;
    }
    setProposalLoading(true);
    try {
      const [proposals, revisions] = await Promise.all([
        getPendingScheduleProposals(schedule.schedule_id),
        getScheduleRevisions(schedule.schedule_id, 20),
      ]);
      setActiveScheduleProposal(proposals[0] || null);
      setScheduleRevisions(revisions);
    } catch (versioningError) {
      console.warn('Schedule version history is unavailable:', versioningError);
      setActiveScheduleProposal(null);
      setScheduleRevisions([]);
    } finally {
      setProposalLoading(false);
    }
  }, [schedule?.schedule_id, schedule?.user_id, userId]);

  useEffect(() => {
    void refreshScheduleVersioning();
    const refresh = () => { void refreshScheduleVersioning(); };
    window.addEventListener('assistant:schedule-proposal-created', refresh);
    window.addEventListener('assistant:schedule-proposal-resolved', refresh);
    return () => {
      window.removeEventListener('assistant:schedule-proposal-created', refresh);
      window.removeEventListener('assistant:schedule-proposal-resolved', refresh);
    };
  }, [refreshScheduleVersioning]);

  const handleApplyScheduleProposal = useCallback(async (proposalId: string) => {
    setProposalApplying(true);
    try {
      const result = await applyScheduleChangeProposal(proposalId);
      if (result.status === 'stale') showToast('This proposal is based on an older schedule. Ask Scarlet Sync to refresh it.', 'error');
      else showToast('Schedule proposal applied as a new version.', 'success');
      await refreshScheduleVersioning();
    } catch (proposalError) {
      showToast(proposalError instanceof Error ? proposalError.message : 'Could not apply the proposal.', 'error');
    } finally {
      setProposalApplying(false);
    }
  }, [refreshScheduleVersioning]);

  const handleDismissScheduleProposal = useCallback(async (proposalId: string) => {
    setProposalApplying(true);
    try {
      await dismissScheduleChangeProposal(proposalId);
      showToast('Schedule proposal dismissed.', 'info');
      await refreshScheduleVersioning();
    } catch (proposalError) {
      showToast(proposalError instanceof Error ? proposalError.message : 'Could not dismiss the proposal.', 'error');
    } finally {
      setProposalApplying(false);
    }
  }, [refreshScheduleVersioning]);

  const handleRestoreScheduleRevision = useCallback(async (revisionId: string) => {
    setProposalApplying(true);
    try {
      await restoreScheduleRevision(revisionId);
      showToast('Older schedule restored as a new version.', 'success');
      await refreshScheduleVersioning();
    } catch (restoreError) {
      showToast(restoreError instanceof Error ? restoreError.message : 'Could not restore this version.', 'error');
    } finally {
      setProposalApplying(false);
    }
  }, [refreshScheduleVersioning]);

  const scheduleViewControl = (
    <div className="flex flex-wrap items-center gap-2">
      {closedDisplayedCourses.length ? <button type="button" onClick={openSnipePicker} className="scheduling-snipe-action inline-flex min-h-10 items-center gap-2 rounded-control border px-3 text-sm font-semibold transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"><TargetIcon className="h-4 w-4" />Snipe</button> : null}
      <SegmentedControl
        value={schedulePresentation}
        onChange={setSchedulePresentation}
        label="Schedule presentation"
        size="sm"
        options={[
          { value: 'grid', label: 'Grid', icon: <Squares2X2Icon className="h-4 w-4" /> },
          { value: 'list', label: 'List', icon: <ListBulletIcon className="h-4 w-4" /> },
          { value: 'finals', label: 'Finals', icon: <CalendarDaysIcon className="h-4 w-4" /> },
        ]}
      />
    </div>
  );

  const openCourseInsight = useCallback((course: ScheduledCourse) => {
    setFocusedInsightCourseId(course.id || course.baseId);
    setWorkspaceTab('information');
  }, []);

  const openCoursePlanner = useCallback(() => {
    if (isMobile) {
      setShowMobilePreferences(true);
      return;
    }
    window.requestAnimationFrame(() => document.getElementById('schedule-course-search')?.focus());
  }, [isMobile]);

  const askScheduleAI = useCallback((prompt: string, course?: ScheduledCourse) => {
    const contextCourses = (course ? [course] : displayedScheduleCourses).map((entry) => ({
      baseId: entry.baseId,
      name: entry.name,
      section: entry.section,
      instructors: entry.instructors.map((instructor) => typeof instructor === 'string' ? instructor : instructor.name),
      campus: entry.campus,
    }));
    openAssistant({
      initialPrompt: prompt,
      autoSend: true,
      startNewChat: true,
      source: 'scheduling-course-insights',
      scheduleCourses: contextCourses,
    });
  }, [displayedScheduleCourses, openAssistant]);

  useEffect(() => {
    if (!schedule?.schedule_id || !schedule?.term_id) return;
    setPreferredScheduleForSemester(schedule.term_id, schedule.schedule_id);
  }, [schedule?.schedule_id, schedule?.term_id, setPreferredScheduleForSemester]);

  const blankCreationTerm = schedule?.term_id || activeSemester;
  const blankCreationTermLabel = SEMESTERS.find((semester) => semester.id === blankCreationTerm)?.label || 'New';
  const blankCreation = useBlankScheduleCreation({
    ownerId: user?.id ?? null,
    scheduleId: schedule?.user_id === user?.id && schedule?.schedule_id === scheduleId ? scheduleId : null,
    routeKey: scheduleRouteKey, term: blankCreationTerm, name: `My ${blankCreationTermLabel} Schedule`,
    enabled: isViewingSelectedSchedule && !authLoading, primaryScheduleId,
    canStart: () => !scheduleId || hydration.canWrite(),
    isRouteCurrent: () => typeof window === 'undefined' || (window.location.pathname === creationPathname
      && new URLSearchParams(window.location.search).toString() === creationQuery),
    createSchedule: payload => schedulesApi.create(payload),
    assignPrimary: id => usersApi.update({ p_primary_schedule_id: id }),
    navigate: id => router.push(`/scheduling?id=${id}`),
  });

  // Fetch friends' primary schedules (cached 15m) and compute overlaps
  useEffect(() => {
    if (MOCK_FRIENDS) return; // skip API when mocking
    const loadFriendsSchedules = async () => {
      if (!user?.id) return;
      try {
        const friends = await socialApi.getFriendsPrimarySchedules(user.id);
      const currentTerm = schedule?.term_id || activeSemester;

        // Build presence map by exact section id (registrationIndex)
        const presence: Record<string, { avatarUrl?: string; displayName?: string; userId: string }[]> = {};
        const sameCourseBaseIds = new Set<string>();

        // IMPORTANT: Only overlay schedules that match the current semester
        const relevantFriends = friends.filter(f => {
          // Check if primary schedule matches the term
          if (f.primarySchedule?.term_id === currentTerm) return true;
          
          // Otherwise, check if they have ANY schedule for this term
          const termSchedule = (f.schedules || []).find((s: any) => s.term_id === currentTerm);
          if (termSchedule) {
            // Swap the primarySchedule reference for this UI view to the relevant one
            f.primarySchedule = {
              schedule_id: termSchedule.schedule_id,
              classes: termSchedule.classes || [],
              name: termSchedule.name || 'Schedule',
              term_id: termSchedule.term_id || currentTerm
            };
            return true;
          }
          return false;
        });

        setFriendsWithSchedules(relevantFriends);

        relevantFriends.forEach(f => {
          const classes = f.primarySchedule?.classes || [];
          classes.forEach((entry: string) => {
            const [baseId, sectionId] = (entry || '').split(':');
            if (!sectionId) return;
            if (!presence[sectionId]) presence[sectionId] = [];
            presence[sectionId].push({
              avatarUrl: f.friend.profile_picture_url,
              displayName: f.friend.display_name || f.friend.name,
              userId: f.friend.id,
            });
            if (baseId) sameCourseBaseIds.add(baseId);
          });
        });

        setFriendPresenceBySection(presence);
        setFriendsSameCourseBaseIds(sameCourseBaseIds);
      } catch (e) {
        // non-fatal
        console.warn('Failed to load friends schedules', e);
      }
    };
    loadFriendsSchedules();
  }, [user?.id, schedule?.term_id]);

  // Mock friend presence and list overlaps for UI preview
  useEffect(() => {
    if (!MOCK_FRIENDS) return;
    const currentCourses: ScheduledCourse[] = (isViewingSelectedSchedule && schedule?.classes
      ? (schedule.classes.map(classId => {
        const [baseId, sectionId] = classId.split(':');
        const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
        return course ? mapRutgersCourseToScheduledCourse(course) : null;
      }).filter(Boolean) as ScheduledCourse[])
      : generatedSchedules[currentScheduleIndex]?.courses || []);

    if (!currentCourses || currentCourses.length === 0) return;

    const presence: Record<string, { avatarUrl?: string; displayName?: string; userId: string }[]> = {};
    const baseIds = new Set<string>();

    const first = currentCourses[0];
    presence[first.id] = [
      { userId: 'u1', displayName: 'Alex', avatarUrl: '/android-chrome-192x192.png' },
      { userId: 'u2', displayName: 'Sam', avatarUrl: '/apple-touch-icon.png' },
      { userId: 'u3', displayName: 'Jamie', avatarUrl: '/favicon-32x32.png' },
      { userId: 'u4', displayName: 'Casey', avatarUrl: '/favicon-16x16.png' },
    ];
    baseIds.add(first.baseId);

    if (currentCourses[1]) {
      const second = currentCourses[1];
      presence[second.id] = [
        { userId: 'u5', displayName: 'Taylor', avatarUrl: '/favicon.ico' },
      ];
      baseIds.add(second.baseId);
    }

    setFriendPresenceBySection(presence);
    setFriendsSameCourseBaseIds(baseIds);

    // Mock my course lists to trigger friend-in-list notice
    // Note: This is now handled by the useCourseLists hook
  }, [MOCK_FRIENDS, isViewingSelectedSchedule, schedule?.classes, generatedSchedules, currentScheduleIndex, allCourses]);

  // Memoize expensive computations
  const memoizedAllCourses = useMemo(() => allCourses, [allCourses]);

  // Memoize course lookup function
  const getCourseDataByClassNumberMemoized = useCallback((baseId: string) => {
    return getCourseDataByClassNumber(memoizedAllCourses, baseId);
  }, [memoizedAllCourses]);

  // Memoize selected courses to prevent unnecessary re-renders
  const memoizedSelectedCourses = useMemo(() => selectedCourses, [selectedCourses]);
  const memoizedSelectedCoursesList = useMemo(() => selectedCoursesList, [selectedCoursesList]);
  const memoizedPreferences = useMemo(() => preferences, [preferences]);

  const triggerRegenerationIndicator = useCallback(() => {
    if (regenerationIndicatorTimeoutRef.current) {
      clearTimeout(regenerationIndicatorTimeoutRef.current);
    }
    setShowRegenerationIndicator(true);
    regenerationIndicatorTimeoutRef.current = setTimeout(() => {
      setShowRegenerationIndicator(false);
      regenerationIndicatorTimeoutRef.current = null;
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      if (regenerationIndicatorTimeoutRef.current) {
        clearTimeout(regenerationIndicatorTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to extract friend schedules for scoring
  const getFriendSchedulesForScoring = useCallback(() => {
    if (!friendsWithSchedules || friendsWithSchedules.length === 0) return undefined;

    return friendsWithSchedules
      .map(friend => {
        const classes = friend.primarySchedule?.classes || [];
        return classes
          .map((entry: string) => {
            const [baseId, sectionId] = (entry || '').split(':');
            if (!sectionId) return null;
            const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
            return course ? mapRutgersCourseToScheduledCourse(course) : null;
          })
          .filter(Boolean) as ScheduledCourse[];
      })
      .filter(schedule => schedule.length > 0);
  }, [friendsWithSchedules, allCourses]);

  // Remove separate info notices; integrated into warnings list below for unified UX

  // Build Set of course IDs from course lists for visual indication
  // Include all sections from course lists, as these are the courses that can appear in generated schedules
  const coursesFromCourseLists = useMemo(() => {
    const courseIdsSet = new Set<string>();
    selectedCourses.forEach(course => {
      if (course.isCourseList && course.sections) {
        course.sections.forEach(section => {
          // Add both the section ID and baseId for matching
          courseIdsSet.add(section.id);
          courseIdsSet.add(section.baseId);
          // Also add normalized versions for reliable matching
          // Handle both colon format (01:640:356) and digits-only format (01640356)
          if (section.id.includes(':')) {
            const parts = section.id.split(':');
            if (parts.length >= 3) {
              const baseIdColon = parts.slice(0, 3).join(':');
              courseIdsSet.add(baseIdColon);
              const digitsOnly = baseIdColon.replace(/\D/g, '');
              if (digitsOnly.length >= 8) {
                courseIdsSet.add(digitsOnly);
              }
            }
          } else {
            // If digits-only format, also add colon format
            const digits = section.id.replace(/\D/g, '');
            if (digits.length >= 8) {
              const school = digits.slice(0, 2);
              const subject = digits.slice(2, 5);
              const number = digits.slice(5, 8);
              courseIdsSet.add(`${school}:${subject}:${number}`);
            }
          }
          // Same normalization for baseId
          if (section.baseId.includes(':')) {
            const digitsOnly = section.baseId.replace(/\D/g, '');
            if (digitsOnly.length >= 8) {
              courseIdsSet.add(digitsOnly);
            }
          } else {
            const digits = section.baseId.replace(/\D/g, '');
            if (digits.length >= 8) {
              const school = digits.slice(0, 2);
              const subject = digits.slice(2, 5);
              const number = digits.slice(5, 8);
              courseIdsSet.add(`${school}:${subject}:${number}`);
            }
          }
        });
      }
    });
    return courseIdsSet;
  }, [selectedCourses]);

  // Configuration data for auto-save (configuration + metadata)
  const configurationDataForAutosave = useMemo(() => {
    if (!schedule) return null;

    const simplifiedSelectedCourses = selectedCourses.map(course => ({
      id: course.id,
      name: course.name,
      selectedSectionId: course.selectedSection?.id,
      isCourseList: course.isCourseList,
      courseListId: course.courseList?.id,
      quantity: course.quantity,
      allowedSections: course.allowedSections,
      allowedCourses: course.allowedCourses
    }));

    const configuration: ScheduleConfiguration = {
      selectedCourses: simplifiedSelectedCourses,
      pinnedClasses,
      currentScheduleIndex: currentScheduleIndex,
      selectedCourseIds: selectedCourses.map(course => course.id),
      customActivities,
    };

    return {
      scheduleId: schedule.schedule_id,
      classes: [...(schedule.classes ?? [])],
      name: tempName,
      is_public: schedule.is_public,
      is_primary: schedule.schedule_id === primaryScheduleId,
      configuration: configuration,
    };
  }, [schedule, selectedCourses, currentScheduleIndex, pinnedClasses, tempName, primaryScheduleId, customActivities]);


  // Autosave configuration changes and metadata
  const { forceSave: forceSaveSchedule, markAsChanged: markAutosaveChanged, isSaving, hasUnsavedChanges: hasUnsavedConfigurationChanges } = useAutoSave(configurationDataForAutosave, {
    writeScope: 'schedule-configuration',
    contextKey: user?.id && schedule?.user_id === user.id && schedule?.schedule_id === scheduleId
      ? JSON.stringify([user.id, scheduleId, configurationSaveRouteKey, catalogTerm, hydration.acceptedCommitRevision]) : null,
    // An admitted chain retains its old operation until settlement. New attempts
    // are excluded by the held physical slot and the explicit dispatch refusal.
    enabled: hydration.phase === 'ready' || getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined,
    delay: 3000, // 3 second delay for configuration changes
    onSave: async (data, operation) => {
      if (!hydration.canWrite()) throw new Error('Loading saved changes. Saving is paused.');
      if (!data || !user || !operation.isCurrent()) return;
      await saveScheduleConfiguration(data, operation, {
        ownerId: user.id, primaryScheduleId,
        writeSchedule: (id, updates) => schedulesApi.update(id, updates),
        writePrimary: (id) => usersApi.update({ p_primary_schedule_id: id }),
        publishSchedule: (ownerId, id, updates) => setSchedule(previous =>
          previous?.user_id === ownerId && previous.schedule_id === id ? { ...previous, ...updates } : previous),
        publishPrimary: setPrimaryScheduleId,
        publishSaved: () => { setLastSaved(new Date()); setHasUnsavedChanges(false); },
      });
    },
    onSaveError: (error) => {
      console.error('Failed to auto-save configuration:', error);
    }
  });

  // Full-route autosave retirement stays intact. A retained remote mismatch
  // remains visibly unsaved/paused even if that raw route baseline resets.
  const hasUnsavedWorkspaceChanges = hasUnsavedChanges || hasUnsavedConfigurationChanges || hydration.phase === 'conflict';

  // Record valid human intent once, including unblurred names. Disabled autosave
  // retains this same-context edit; only an accepted hydration changes its baseline.
  const markAsChanged = useCallback(() => {
    if (!scheduleId || hydration.markEdit()) markAutosaveChanged();
  }, [scheduleId, hydration.markEdit, markAutosaveChanged]);

  // The selected schedule write owns a committed destination and draft lifetime.
  const selectedSaveClasses = useMemo(() => schedule?.classes ?? [], [schedule?.classes]);
  const selectedSaveDraft = useMemo(() => ({
    classes: selectedSaveClasses, name: schedule?.name, tempName, isPublic: schedule?.is_public,
    selectedCourses, pinnedClasses, customActivities, currentScheduleIndex, isViewingSelectedSchedule, generatedSchedules,
  }), [selectedSaveClasses, schedule?.name, tempName, schedule?.is_public, selectedCourses, pinnedClasses, customActivities, currentScheduleIndex, isViewingSelectedSchedule, generatedSchedules]);
  const writeSelectedClasses = useCallback((id: string, classes: string[]) => schedulesApi.update(id, { classes }), []);
  const commitSelectedSave = useCallback((identity: SelectedScheduleSaveIdentity, classes: string[]) => {
    setSchedule(previous => previous?.user_id === identity.ownerId && previous?.schedule_id === identity.scheduleId ? { ...previous, classes } : previous);
    setLastSaved(new Date());
    setHasUnsavedChanges(false);
    trackScheduleSaved(classes.length, 'selected_schedule');
  }, []);
  const selectedSave = useSelectedScheduleSave({
    ownerId: user?.id ?? null,
    scheduleId: schedule?.user_id === user?.id && schedule?.schedule_id === scheduleId ? scheduleId : null,
    routeKey: scheduleRouteKey, term: catalogTerm,
    draft: selectedSaveDraft, classes: selectedSaveClasses,
    isBlocked: () => !hydration.canWrite() || isSaving || semesterChange.isPending,
    writeClasses: writeSelectedClasses, onCommit: commitSelectedSave,
    onError: error => console.error('Failed to save selected schedule:', error),
  });
  const isSavingSchedule = isSaving || selectedSave.isPending;
  const handleSaveSelectedSchedule = selectedSave.save;
  // Consumed add commands share the configuration destination; other route
  // replacements retire in-flight catalog continuations before local edits.
  const removalAuthority = useScheduleWriteAuthority({
    ownerId: user?.id ?? null,
    scheduleId: schedule?.user_id === user?.id && schedule?.schedule_id === scheduleId ? scheduleId : null,
    routeKey: configurationSaveRouteKey, term: catalogTerm, draft: selectedSaveDraft,
  });
  const isSavingGeneratedSchedule = removalAuthority.isPending;
  const removalIntents = useCourseRemovalIntent(JSON.stringify([user?.id ?? null,
    schedule?.user_id === user?.id && schedule?.schedule_id === scheduleId ? scheduleId : null,
    configurationSaveRouteKey, catalogTerm]), selectedCourses);


  // Full owned load is not restarted by its own row/catalog publication.
  useEffect(() => {
    if (hydration.phase === 'ready' && scheduleId && !authLoading
      && !(schedule && user && schedule.user_id === user.id && schedule.schedule_id === scheduleId
        && loadedCatalogTerm === schedule.term_id
        && initializedScheduleKey.current === `${schedule.schedule_id}:${schedule.term_id}`)) {
      void hydration.load('initial');
    }
  }, [scheduleId, authLoading, user?.id, configurationSaveRouteKey, hydration.phase, hydration.load]);

  useEffect(() => {
    if (scheduleId) return;
    let cancelled = false;
    const initializeSchedule = async () => {
        // ** NEW LOGIC: No schedule ID in URL, show schedule selection modal **
        try {
          scheduleDebugGroupCollapsed('Scheduling: No ID → Open Modal');
          scheduleDebug('User:', user?.id);
          if (!user?.id) {
            setError('Authentication required');
            setIsLoading(false);
            return;
          }

          // Fetch user schedules and show modal
          // The directory refresh effect owns this read and its owner/route
          // fences; there is no asynchronous dialog-opening continuation.
          if (cancelled) return;
          if (!chooserDismissed.current) setShowScheduleModal(true);
          setIsModalOpenedManually(false); // Not manually opened
          setIsLoading(false);
          scheduleDebugGroupEnd();
        } catch (err) {
          if (cancelled) return;
          console.error("Failed to initialize schedule selection:", err);
          // Don't set error, just show modal with empty schedules
          setShowScheduleModal(true);
          setIsModalOpenedManually(false); // Not manually opened
          setIsLoading(false);
        }
    };
    if (allCourses.length > 0 && loadedCatalogTerm && !authLoading) {
      setIsLoading(true); void initializeSchedule();
    }
    return () => { cancelled = true; };
  }, [scheduleId, router, allCourses, loadedCatalogTerm, authLoading, user?.id]);

  // Changed stage contract: the original pre-read receipt now owns target catalog
  // and projection too. Old isolated Assistant23 proof remains historical only.
  useAssistantScheduleRefresh({ refresh: id => hydration.load('assistant', id) });

  // Keep local preference state in sync when Ask Scarlet Sync updates preferences_json.
  useEffect(() => {
    const handlePreferencesUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ preferences?: PreferencesType }>).detail;
      const next = detail?.preferences;
      if (!next || typeof next !== 'object') return;
      setPreferences((prev) => ({
        ...(prev ?? {
          availability: Array(5).fill(null).map(() => Array(AVAILABILITY_SLOT_COUNT).fill(true)),
          preferredCampuses: [],
          earliestStartTime: 9 * 60,
          latestEndTime: 22 * 60,
          minimumProfessorRating: 0,
          priorities: [],
          campusPreferences: [],
        }),
        ...next,
        availability: Array.isArray(next.availability) ? next.availability : (prev?.availability ?? Array(5).fill(null).map(() => Array(AVAILABILITY_SLOT_COUNT).fill(true))),
      }));
    };

    window.addEventListener('assistant:preferences-updated', handlePreferencesUpdate);
    return () => {
      window.removeEventListener('assistant:preferences-updated', handlePreferencesUpdate);
    };
  }, []);

  // Fetch user preferences
  useEffect(() => {
    let cancelled = false;
    const owner = user?.id;
    setPreferencesOwner(null);
    setPreferencesLoadError(null);
    const fetchPrefs = async () => {
      if (!owner) return;
      const userData = await readOwnedProfile(owner);
      if (cancelled) return;
      const savedPreferences = 'preferences_json' in userData ? userData.preferences_json : null;
      if (savedPreferences && typeof savedPreferences === 'object'
        && 'preferredCampuses' in savedPreferences && Array.isArray(savedPreferences.preferredCampuses)) {
        setPreferences(savedPreferences as PreferencesType);

      } else {
        // Set default preferences if none exist
        scheduleDebug('setting default preferences');
        scheduleDebug('Using local default preferences');

        setPreferences({
          availability: Array(5).fill(null).map(() => Array(AVAILABILITY_SLOT_COUNT).fill(true)),
          preferredCampuses: ['NB'],
          earliestStartTime: 9 * 60,
          latestEndTime: 22 * 60,
          minimumProfessorRating: 0,
          priorities: [
            { name: 'campus', value: 33, color: 'bg-pink-500' },
            { name: 'professor ratings', value: 33, color: 'bg-purple-500' },
            { name: 'times', value: 34, color: 'bg-blue-500' },
          ],
          campusPreferences: [
            { name: 'college ave', value: 20, color: 'bg-pink-500' },
            { name: 'livi', value: 20, color: 'bg-purple-500' },
            { name: 'busch', value: 20, color: 'bg-indigo-500' },
            { name: 'cook/doug', value: 20, color: 'bg-blue-500' },
            { name: 'online', value: 20, color: 'bg-teal-500' },
          ],
          disableLogisticsPenalties: true,
        });
      }
      setPreferencesOwner(owner);
    };
    void fetchPrefs().catch(() => { if (!cancelled) setPreferencesLoadError('Preferences could not be loaded.'); });
    return () => { cancelled = true; };
  }, [user?.id, generationInputRetry, readOwnedProfile]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setOpenSectionsLoadingTerm(openSectionsTermId);
    setOpenSectionsLoadError(null);
    const settled = () => {
      if (cancelled) return;
      setOpenSectionsRevision(revision => revision + 1);
      setOpenSectionsLoadingTerm(null);
      if (!openSectionsService.openSectionsCache.has(openSectionsTermId)) setOpenSectionsLoadError('Section availability could not be loaded. Retry or turn off Open sections.');
    };
    void openSectionsService.refreshOpenSections(openSectionsTermId).then(settled, settled);
    return () => { cancelled = true; };
  }, [userId, openSectionsTermId, generationInputRetry]);

  // Reconstruct sections for loaded courses when allCourses becomes available


  useEffect(() => {
    if (allCourses.length > 0 && selectedCourses.length > 0 && !hasReconstructedSections) {
      // Check if any courses need section reconstruction
      const needsReconstruction = selectedCourses.some(course =>
        !course.sections || course.sections.length === 0
      );

      if (needsReconstruction) {
        const reconstructedSelectedCourses = selectedCourses
          .map(course => {
            // Skip course lists - they're handled separately
            if (course.isCourseList) {
              return course;
            }

            // If the course doesn't have sections or has empty sections, reconstruct them
            if (!course.sections || course.sections.length === 0) {
              // Normalize the baseId and convert to format used by RutgersCourse.baseId (without colons)
              const normalizedBaseId = normalizeBaseId(course.id);
              const baseIdForLookup = normalizedBaseId.replace(/:/g, '');
              // Try to find the course in allCourses and reconstruct sections
              const allSectionsForCourse = getCourseDataByClassNumberMemoized(baseIdForLookup);
              if (allSectionsForCourse.length > 0) {
                const sections = allSectionsForCourse.map(mapRutgersCourseToScheduledCourse);
                return {
                  ...course,
                  sections: sections,
                  selectedSection: course.selectedSection || course.selectedSectionId
                    ? sections.find(s => s.id === course.selectedSectionId) || sections[0]
                    : sections[0],
                  allowedSections: course.allowedSections,
                  allowedCourses: course.allowedCourses
                };
              }
            }
            return course;
          })
          .filter((course): course is GroupedCourse => course !== null);

        setSelectedCourses(reconstructedSelectedCourses);
        setHasReconstructedSections(true);
      }
    }
  }, [allCourses, selectedCourses, hasReconstructedSections, getCourseDataByClassNumberMemoized]);

  // Reconstruct course list sections when course list data becomes available


  useEffect(() => {
    scheduleDebugGroupCollapsed('Scheduling: Course List Reconstruction');
    scheduleDebug('allCourses:', allCourses.length, 'courseLists:', courseLists.length, 'myCourseLists:', myCourseLists.length, 'selectedCourses:', selectedCourses.length, 'hasReconstructedCourseLists:', hasReconstructedCourseLists);

    if (allCourses.length > 0 && !courseListsLoading && !courseListsLoadError && completedCoursesOwner === user?.id && !completedCoursesLoadError && selectedCourses.length > 0 && !hasReconstructedCourseLists) {
      scheduleDebug('All data available for reconstruction');

      const courseListsNeedingReconstruction = selectedCourses.filter(course => {
        if (!course.isCourseList) return false;
        return !course.sections || course.sections.length === 0;
      });

      scheduleDebug('Course lists needing reconstruction:', courseListsNeedingReconstruction.map(c => ({
        id: c.id,
        name: c.name,
        courseListId: c.courseList?.id,
        sectionsLength: c.sections?.length || 0
      })));

      // Debug: Check if the course lists we need are in the fetched data
      const neededIds = courseListsNeedingReconstruction.map(c => c.courseList?.id);
      scheduleDebug('Looking for course list IDs:', neededIds);
      const allAvailableCourseLists = [...courseLists, ...myCourseLists];
      const foundCourseLists = allAvailableCourseLists.filter(cl => neededIds.includes(cl.id.toString()));
      scheduleDebug('Found course lists:', foundCourseLists.map(cl => ({ id: cl.id, name: cl.name, isUserList: !!cl.user_generated })));

      scheduleDebug('Lists needing reconstruction:', courseListsNeedingReconstruction.length);
      courseListsNeedingReconstruction.forEach(course => {
        scheduleDebugGroupCollapsed('Needs reconstruction');
        scheduleDebug({
          id: course.id,
          name: course.name,
          isCourseList: course.isCourseList,
          sectionsLength: course.sections?.length || 0,
          courseListId: course.courseList?.id
        });
        scheduleDebugGroupEnd();
      });

      if (courseListsNeedingReconstruction.length > 0) {
        scheduleDebug('Starting reconstruction…');

        const reconstructedSelectedCourses = selectedCourses.map(course => {
          const hasNoSections = !course.sections || course.sections.length === 0;
          if (course.isCourseList && course.courseList && hasNoSections) {
            scheduleDebugGroupCollapsed('Reconstruct: Course List');
            scheduleDebug({
              id: course.id,
              name: course.name,
              courseListId: course.courseList?.id,
              sectionsLength: course.sections?.length || 0
            });

            // Find the actual course list data from the fetched course lists
            scheduleDebug('Looking for courseListId:', course.courseList?.id);

            // Search in both official course lists and user course lists
            const allAvailableCourseLists = [...courseLists, ...myCourseLists];
            const actualCourseList = allAvailableCourseLists.find(cl => {
              const clId = cl.id.toString();
              const courseListId = course.courseList?.id?.toString();
              // Only log comparisons for the course list we're looking for to reduce noise
              if (clId === courseListId) {
                scheduleDebug('Found match:', { clId, courseListId, name: cl.name, isUserList: !!cl.user_generated });
              }
              return clId === courseListId;
            });
            scheduleDebug('Found actual course list:', !!actualCourseList);

            if (actualCourseList) {
              let courseListSections: ScheduledCourse[];

              // If sections already exist but allowedCourses is empty, use existing sections
              // Otherwise, reconstruct sections from course list data
              if (!hasNoSections && course.sections && course.sections.length > 0) {
                scheduleDebug('Using existing sections, rebuilding allowedCourses');
                courseListSections = course.sections;
              } else {
                scheduleDebug('Reconstructing sections for:', actualCourseList.name);

                // Helper function to normalize baseId to digits-only format
                const normalizeBaseId = (courseId: string): string => {
                  const digits = courseId.replace(/\D/g, '');
                  if (digits.length >= 8) {
                    return digits; // Return digits-only format
                  }
                  return courseId;
                };

                // Reconstruct sections for course lists using the actual course list data
                courseListSections = [];
                const uniqueCourseIds = new Set<string>();

                actualCourseList.classes.forEach((courseId: string) => {
                  if (!uniqueCourseIds.has(courseId)) {
                    uniqueCourseIds.add(courseId);
                    // Use normalizedBaseId for lookup
                    const normalizedBaseId = normalizeBaseId(courseId);
                    const allSections = getCourseDataByClassNumberMemoized(normalizedBaseId);
                    if (allSections.length > 0) {
                      allSections.forEach(section => {
                        courseListSections.push(mapRutgersCourseToScheduledCourse(section));
                      });
                    }
                  }
                });

                scheduleDebug('Sections reconstructed:', courseListSections.length);
              }

              if (courseListSections.length > 0) {
                scheduleDebug('Rebuilding allowedCourses');

                // Try to find the previously selected section if we have a selectedSectionId
                let selectedSection = course.selectedSection || courseListSections[0];
                if (course.selectedSectionId && hasNoSections) {
                  const savedSection = courseListSections.find(s => s.id === course.selectedSectionId);
                  if (savedSection) {
                    selectedSection = savedSection;
                    scheduleDebug('Restored selected section:', savedSection.name);
                  } else {
                    scheduleDebug('Could not find saved section ID:', course.selectedSectionId);
                  }
                }

                const normalizedAllowedCourses = restoreCourseListEligibility(
                  courseListSections, course.allowedCourses, completedCourseBaseIds,
                );

                return {
                  ...course,
                  sections: courseListSections,
                  selectedSection: selectedSection,
                  courseList: actualCourseList, // Update with the actual course list data
                  allowedSections: course.allowedSections,
                  allowedCourses: normalizedAllowedCourses
                };
              } else {
                scheduleDebug('No sections available');
              }
            } else {
              scheduleDebug('No actual course list data found');
            }
            scheduleDebugGroupEnd();
          }
          return course;
        });

        // Only update if we actually reconstructed something
        const hasChanges = reconstructedSelectedCourses.some((course, index) =>
          course !== selectedCourses[index]
        );

        scheduleDebug('Has changes:', hasChanges);
        scheduleDebug('Reconstruction summary:', reconstructedSelectedCourses.map(c => ({
          id: c.id,
          name: c.name,
          isCourseList: c.isCourseList,
          sectionsLength: c.sections?.length || 0,
          hasSelectedSection: !!c.selectedSection
        })));

        if (hasChanges) {
          scheduleDebug('Updating selected courses with reconstructed data');
          setSelectedCourses(reconstructedSelectedCourses);
        } else {
          scheduleDebug('No changes detected, skipping update');
        }

        // Mark as reconstructed to prevent infinite loops
        setHasReconstructedCourseLists(true);
      } else {
        scheduleDebug('No course lists need reconstruction');
        setHasReconstructedCourseLists(true);
      }
    } else {
      scheduleDebug('Waiting for required data:', {
        allCourses: allCourses.length > 0,
        courseLists: courseLists.length > 0,
        myCourseLists: myCourseLists.length > 0,
        selectedCourses: selectedCourses.length > 0,
        hasReconstructedCourseLists
      });
    }
    scheduleDebugGroupEnd();
  }, [allCourses, courseLists, myCourseLists, courseListsLoading, courseListsLoadError, selectedCourses, hasReconstructedCourseLists, completedCourseBaseIds, completedCoursesOwner, completedCoursesLoadError, user?.id]);

  const [completedNoScheduleReasons, setCompletedNoScheduleReasons] = useState<import('../types/interfaces').NoScheduleReason[] | null>(null);
  const generationInput = useMemo<WorkspaceGenerationInput>(() => ({
    groupedCourses: selectedCourses.map(group => group.isCourseList ? {
      ...group, allowedCourses: restoreCourseListEligibility(group.sections, group.allowedCourses, completedCourseBaseIds),
    } : group),
    courseLists: selectedCoursesList,
    pinnedClasses,
    generateOnlyOpenSections,
    preferences: memoizedPreferences,
    openSectionsTermId,
    friendSchedulesForScoring: getFriendSchedulesForScoring(),
    customActivityCourses,
    openRevision: openSectionsRevision,
  }), [selectedCourses, selectedCoursesList, pinnedClasses, generateOnlyOpenSections, memoizedPreferences, openSectionsTermId, getFriendSchedulesForScoring, customActivityCourses, openSectionsRevision, completedCourseBaseIds]);
  // Preserve input order (search tie order) and every eligibility/scoring field,
  // including explicit empty allowed maps, section data, term and friend inputs.
  const currentGenerationSignature = useMemo(() => JSON.stringify(generationInput), [generationInput]);
  const selectedListsReady = !selectedCourses.some(group => group.isCourseList)
    || (!courseListsLoading && !courseListsLoadError && hasReconstructedCourseLists && completedCoursesOwner === user?.id && !completedCoursesLoadError);
  const availabilityReady = !generateOnlyOpenSections || openSectionsService.openSectionsCache.has(openSectionsTermId);
  const generationReady = Boolean(!isGenerationBlockedByScheduleWrite(semesterChange.isPending || hydration.phase !== 'ready', ownDirectSaveOperation.current ?? blankCreation.getActiveToolbarOperation(), {
    ownerId: user?.id ?? null,
    scheduleId: schedule?.user_id === user?.id && schedule?.schedule_id === scheduleId ? scheduleId : null,
    routeKey: scheduleRouteKey, term: catalogTerm,
  }) && selectedListsReady && availabilityReady && preferencesOwner === user?.id
    && user?.id && schedule?.user_id === user.id
    && schedule.schedule_id === scheduleId && !isLoading && loadedCatalogTerm === catalogTerm
    && initializedScheduleKey.current === `${schedule.schedule_id}:${catalogTerm}`);
  const runGeneration = useCallback(async (input: WorkspaceGenerationInput, options: { signal: AbortSignal; deadlineMs: number }) => {
    const totalCourses = input.groupedCourses.reduce((total, group) => total + (group.isCourseList ? group.quantity || 1 : 1), 0);
    if (totalCourses > SCHEDULE_DISPLAY_LIMITS.MAX_COURSES_FOR_GENERATION) {
      throw new Error(`Select ${SCHEDULE_DISPLAY_LIMITS.MAX_COURSES_FOR_GENERATION} or fewer courses to generate schedules.`);
    }
    return generateScheduleOptionsCooperatively(input, options);
  }, []);
  const commitGeneration = useCallback((result: CooperativeGenerationResult, identity: GenerationIdentity) => {
    setGenerationIdentity(identity);
    setAllScoredSchedules(result.schedules);
    setGeneratedSchedules(result.schedules.slice(0, SCHEDULE_DISPLAY_LIMITS.MAX_SCHEDULES_TO_DISPLAY));
    setTotalSchedulesGenerated(result.schedules.length);
    setCompletedNoScheduleReasons(result.schedules.length ? null : result.noScheduleReasons);
    setCurrentScheduleIndex(0);
    setIsViewingSelectedSchedule(false);
    setError(null);
    triggerRegenerationIndicator();
    trackScheduleGenerated({ schedule_count: result.schedules.length, total_courses: identity.input.groupedCourses.length,
      pinned_classes: identity.input.pinnedClasses.length, generate_only_open: identity.input.generateOnlyOpenSections,
      preferences_set: !!identity.input.preferences });
  }, [triggerRegenerationIndicator]);
  const generation = useScheduleGeneration({
    input: generationInput, contextKey: generationContextKey, signature: currentGenerationSignature,
    enabled: generationReady && selectedCourses.length > 0, paused: isConfigModalOpen,
    generate: runGeneration, onCommit: commitGeneration,
    timeoutMs: SCHEDULE_DISPLAY_LIMITS.SCHEDULE_GENERATION_TIMEOUT,
  });
  const isGenerating = generation.isGenerating;
  const generatedResultsCurrent = generationBelongsToWorkspace && generationIdentity.signature === currentGenerationSignature;
  const canReviewGeneration = generatedResultsCurrent && generation.status === 'completed' && totalSchedulesGenerated === 0;
  const noScheduleReasons = canReviewGeneration ? completedNoScheduleReasons : null;
  useLayoutEffect(() => { if (!canReviewGeneration) setShowNoScheduleModal(false); }, [canReviewGeneration]);
  const generatedSaveDisabled = isGenerating || !generatedResultsCurrent || !generationReady;
  useLayoutEffect(() => { setShowNoScheduleModal(false); }, [currentGenerationSignature]);
  const previousGenerationLabel = generationBelongsToWorkspace && (!generatedResultsCurrent || !generationReady)
    ? `Previous options · ${SEMESTERS.find(term => term.id === generatedTerm)?.label || generatedTerm}` : null;
  const regenerateSchedulesNow = generation.start;
  const generationPreparationError = preferencesLoadError
    || (selectedCourses.some(group => group.isCourseList) ? courseListsLoadError || completedCoursesLoadError : null)
    || (generateOnlyOpenSections ? openSectionsLoadError : null);
  const generationFeedback = <>
    <ScheduleGenerationFeedback status={generationPreparationError ? 'error' : generation.status} onStop={generation.stop}
      focusContextKey={JSON.stringify([generationContextKey, scheduleRouteKey, currentGenerationSignature])}
      error={generationPreparationError || generation.error?.message} previousLabel={previousGenerationLabel} />
    {generationPreparationError ? <Button type="button" variant="secondary" className="mt-2" onClick={() => {
      setGenerationInputRetry(value => value + 1);
      if (selectedCourses.some(group => group.isCourseList)) void loadCourseLists();
    }}>Retry loading</Button> : !generationReady && selectedCourses.length > 0 && (preferencesOwner !== user?.id || !selectedListsReady || (generateOnlyOpenSections && openSectionsLoadingTerm === openSectionsTermId))
      ? <p role="status" className="text-sm text-content-muted">Preparing schedule choices…</p> : null}
  </>;

  useEffect(() => {
    if (!shouldForceRegenerate || isConfigModalOpen || !generationReady) return;
    setShouldForceRegenerate(false);
    regenerateSchedulesNow();
  }, [shouldForceRegenerate, regenerateSchedulesNow, isConfigModalOpen, generationReady]);

  useEffect(() => {
    const fetchUser = async () => {
      if (user) setUserId(user.id);
    };
    fetchUser();
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    const owner = user?.id;
    setCompletedCoursesOwner(null);
    setCompletedCoursesLoadError(null);
    setCompletedCourseBaseIds(new Set());
    if (!owner) return;
    void loadUserAcademicData(owner, { fresh: true }).then(academicData => {
      if (cancelled) return;
      if (!academicData) throw new Error('Academic record is unavailable.');
      setCompletedCourseBaseIds(new Set(academicData.completedCourses.map(course => course.baseId)));
      setCompletedCoursesOwner(owner);
    }).catch(() => {
      if (!cancelled) setCompletedCoursesLoadError('Completed courses could not be loaded.');
    });
    return () => { cancelled = true; };
  }, [user?.id, generationInputRetry]);

  const additionUndoOpenSectionIds = useMemo(() => generateOnlyOpenSections && openSectionsService.openSectionsCache.has(openSectionsTermId)
    ? [...openSectionsService.openSectionsCache.get(openSectionsTermId)!].sort() : null,
    [generateOnlyOpenSections, openSectionsTermId, openSectionsRevision]);
  const additionUndoEditable = useMemo(() => ({
    selectedCourses, storedClasses: schedule?.classes ?? [],
    inputs: { pinnedClasses, customActivities, preferences: memoizedPreferences, generateOnlyOpenSections,
      openSectionIds: additionUndoOpenSectionIds, completedCourseBaseIds: [...completedCourseBaseIds].sort(),
      courseLists: generationInput.courseLists, friendSchedulesForScoring: generationInput.friendSchedulesForScoring,
      tempName, name: schedule?.name, isPublic: schedule?.is_public },
  }), [selectedCourses, schedule?.classes, schedule?.name, schedule?.is_public, pinnedClasses, customActivities,
    memoizedPreferences, generateOnlyOpenSections, tempName, additionUndoOpenSectionIds, completedCourseBaseIds,
    generationInput.courseLists, generationInput.friendSchedulesForScoring]);
  const additionUndo = useCourseAdditionUndo({
    ownerId: user?.id ?? null,
    scheduleId: schedule?.user_id === user?.id && schedule?.schedule_id === scheduleId ? scheduleId : null,
    routeKey: scheduleRouteKey, term: catalogTerm, editable: additionUndoEditable,
    setSelectedCourses, setHasUnsavedChanges, markAsChanged, trackCourseRemoved,
  });
  const handleClassAdd = useMemo(() => createCourseAdditionHandler({
    selectedCourses, schedule, activeSemester, SEMESTERS, getCourseDataByClassNumberMemoized,
    mapRutgersCourseToScheduledCourse, removalIntents, setSelectedCourses, setHasUnsavedChanges,
    markAsChanged, trackCourseAdded, scheduleDebug, getParsedCourses, showToast, onAdded: additionUndo.recordAdded,
  }), [selectedCourses, allCourses, schedule, activeSemester, markAsChanged, removalIntents.invalidate,
    getCourseDataByClassNumberMemoized, showToast, additionUndo.recordAdded]);


  useEffect(() => {
    if (hydration.phase !== 'ready' || !addParamFromUrl || !schedule?.schedule_id || isLoading || !allCourses.length) return;
    if (loadedCatalogTerm !== schedule.term_id || initializedScheduleKey.current !== `${schedule.schedule_id}:${schedule.term_id}`) return;
    const identity = `${schedule.schedule_id}:${addParamFromUrl}:${handoffTerm}`;
    if (handledCourseHandoff.current === identity) return;
    handledCourseHandoff.current = identity;
    if (isSemesterId(handoffTerm) && schedule.term_id !== handoffTerm) {
      showToast('This course was selected in a different term. Choose or create a schedule for that term.', 'error');
      setShowScheduleModal(true);
      setIsModalOpenedManually(true);
      return;
    }
    const baseId = addParamFromUrl.replace(/\D/g, '');
    if (!/^(?:\d{8}|\d{2}:\d{3}:\d{3})$/.test(addParamFromUrl) || !allCourses.some(c => c.baseId.replace(/\D/g, '') === baseId)) {
      showToast('This course is unavailable in this schedule’s catalog. Return to Explore and choose another course.', 'error');
      return;
    }
    if (!selectedCourses.some(c => c.id.replace(/\D/g, '') === baseId)) {
      void handleClassAdd(baseId);
      showToast('Course added to your draft. Review the section and save when ready.', 'success');
    }
    const query = new URLSearchParams(searchParams.toString());
    query.delete('add');
    router.replace(`/scheduling?${query}`);
  }, [hydration.phase, addParamFromUrl, handoffTerm, schedule, isLoading, allCourses, loadedCatalogTerm, initializedScheduleKey, selectedCourses, handleClassAdd, router, searchParams, showToast]);

  const handleClassRemove = useCallback(async (classId: string) => {
    const authority = removalAuthority.captureContext();
    if (!authority) {
      // Placeholder edits have no persistent destination. A retained null-route
      // handler still cannot edit a subsequently committed workspace.
      if (!schedule && removalAuthority.isContextCurrent()) {
        const target = removalCourseId(classId);
        setSelectedCourses(previous => removalAuthority.isContextCurrent() ? previous.filter(course => normalizeBaseId(course.id) !== target) : previous);
        setHasUnsavedChanges(previous => removalAuthority.isContextCurrent() ? true : previous); markAsChanged();
      }
      return;
    }
    const target = removalCourseId(classId);
    const isIntentCurrent = removalIntents.capture(target);
    if (!isIntentCurrent) return;
    const currentAuthority = { identity: authority.identity,
      isContextCurrent: () => authority.isContextCurrent() && isIntentCurrent() };
    await removeScheduleCourse(currentAuthority, classId, {
      removeSelected: (target, current) => {
        setSelectedCourses(previous => current() ? previous.filter(course => normalizeBaseId(course.id) !== target) : previous);
        setHasUnsavedChanges(previous => current() ? true : previous);
        markAsChanged();
        trackCourseRemoved();
      },
      loadCatalog: getParsedCourses,
      removeClasses: (identity, target, catalog, current) => {
        setSchedule(previous => current() && previous?.user_id === identity.ownerId && previous.schedule_id === identity.scheduleId
          ? { ...previous, classes: classesAfterCourseRemoval(previous.classes ?? [], target, catalog) } : previous);
        setHasUnsavedChanges(previous => current() ? true : previous);
        markAsChanged();
      },
      onError: error => console.error('Failed to remove class:', error),
    });
  }, [schedule, removalAuthority.captureContext, removalAuthority.isContextCurrent, removalIntents.capture, markAsChanged]);

  const handleClassPin = useCallback((classId: string) => {
    setPinnedClasses(prev =>
      prev.includes(classId)
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
    setHasUnsavedChanges(true);
    markAsChanged(); // Mark that user made changes
  }, [markAsChanged]);

  const handleCourseListAdd = useCallback((courseList: CourseList) => {

    // 🔍 DEBUG: Log the course list structure
    scheduleDebug('🔍 [COURSE LIST DEBUG] Starting handleCourseListAdd');
    scheduleDebug('🔍 [COURSE LIST DEBUG] Course List:', {
      id: courseList.id,
      name: courseList.name,
      classesCount: courseList.classes?.length || 0,
      classesType: typeof courseList.classes,
      classesIsArray: Array.isArray(courseList.classes),
      classesSample: courseList.classes?.slice(0, 10),
      allClasses: courseList.classes
    });

    // Prevent adding a course list that's already in the list
    if (selectedCourses.some(c => c.isCourseList && c.courseList?.id === courseList.id)) {
      scheduleDebug("Course list already in schedule:", courseList.id);
      return;
    }

    // Helper function to normalize baseId to digits-only format (as stored in allCourses)
    // allCourses uses digits-only format like "01640300", not "01:640:300"
    const normalizeBaseId = (courseId: string): string => {
      // Extract digits only - this matches the format in allCourses
      const digits = courseId.replace(/\D/g, '');
      if (digits.length >= 8) {
        return digits; // Return digits-only format
      }
      // If can't parse, return as-is
      return courseId;
    };

    // Helper function to extract baseId from courseId (for display/comparison)
    const getBaseIdFromCourseId = (courseId: string): string => {
      // Course IDs can be in format "01:198:111", "01:198:111:01", or "01198111"
      // BaseId is the first 3 parts (school:subject:number) in format "01:198:111"
      if (courseId.includes(':')) {
        const parts = courseId.split(':');
        // Take first 3 parts (school:subject:number)
        if (parts.length >= 3) {
          return parts.slice(0, 3).join(':');
        }
        // If less than 3 parts, return as-is (might be malformed)
        return courseId;
      }
      // If no colons, try to parse digits-only format (e.g., "01640452" -> "01:640:452")
      const digits = courseId.replace(/\D/g, '');
      if (digits.length >= 8) {
        const school = digits.slice(0, 2);
        const subject = digits.slice(2, 5);
        const number = digits.slice(5, 8);
        return `${school}:${subject}:${number}`;
      }
      // If can't parse, return as-is
      return courseId;
    };

    // 🔍 DEBUG: Check allCourses structure
    scheduleDebug('🔍 [COURSE LIST DEBUG] allCourses info:', {
      totalCourses: allCourses.length,
      sampleBaseIds: allCourses.slice(0, 20).map(c => ({
        baseId: c.baseId,
        id: c.id,
        courseName: c.courseName
      })),
      baseIdFormats: {
        withColons: allCourses.filter(c => c.baseId.includes(':')).length,
        digitsOnly: allCourses.filter(c => !c.baseId.includes(':')).length
      }
    });

    // Get all existing course baseIds to check for duplicates
    const existingCourseBaseIds = new Set<string>();
    selectedCourses
      .filter(c => !c.isCourseList) // Only check regular courses, not course lists
      .forEach(c => {
        const baseId = getBaseIdFromCourseId(c.id);
        existingCourseBaseIds.add(baseId);
      });

    // Convert course list to GroupedCourse format, filtering out duplicates
    const courseListSections: ScheduledCourse[] = [];
    const uniqueCourseBaseIds = new Set<string>();

    // 🔍 DEBUG: Track processing stats
    const stats = {
      total: 0,
      duplicatesExisting: 0,
      duplicatesInList: 0,
      noSections: 0,
      found: 0,
      sampleProcessed: [] as any[]
    };

    // Get all courses from the course list, filtering out duplicates
    courseList.classes.forEach((courseId: string) => {
      stats.total++;
      const displayBaseId = getBaseIdFromCourseId(courseId); // For display/comparison
      const normalizedBaseId = normalizeBaseId(courseId); // For lookup (digits-only)

      // 🔍 DEBUG: Log ALL courses being processed (not just first 10)
      scheduleDebug(`🔍 [COURSE LIST DEBUG] Processing course ${stats.total}/${courseList.classes.length}:`, {
        originalCourseId: courseId,
        displayBaseId: displayBaseId,
        normalizedBaseId: normalizedBaseId,
        isDuplicateExisting: existingCourseBaseIds.has(displayBaseId),
        isDuplicateInList: uniqueCourseBaseIds.has(displayBaseId)
      });

      // Skip if this course already exists as an individual course (by baseId)
      if (existingCourseBaseIds.has(displayBaseId)) {
        stats.duplicatesExisting++;
        scheduleDebug(`  → Skipping duplicate (already exists as individual course)`);
        return;
      }

      // Skip if we've already added this course to the list (by baseId)
      if (uniqueCourseBaseIds.has(displayBaseId)) {
        stats.duplicatesInList++;
        scheduleDebug(`  → Skipping duplicate (already in course list)`);
        return;
      }

      uniqueCourseBaseIds.add(displayBaseId);
      // Use normalizedBaseId (digits-only) for lookup to match allCourses format
      const allSections = getCourseDataByClassNumberMemoized(normalizedBaseId);

      // 🔍 DEBUG: Log lookup results for ALL courses
      scheduleDebug(`  → Lookup result:`, {
        normalizedBaseId,
        sectionsFound: allSections.length,
        sectionIds: allSections.slice(0, 3).map(s => s.id),
        sectionBaseIds: allSections.slice(0, 3).map(s => s.baseId)
      });

      // Check if normalizedBaseId exists in allCourses at all
      const matchingCourses = allCourses.filter(c => c.baseId === normalizedBaseId);
      scheduleDebug(`  → Direct allCourses search:`, {
        matchesFound: matchingCourses.length,
        sampleMatches: matchingCourses.slice(0, 3).map(c => ({
          baseId: c.baseId,
          id: c.id,
          courseName: c.courseName
        }))
      });

      // Only add courses that have sections (ignore unknown/not offered courses)
      if (allSections.length > 0) {
        stats.found++;
        // Add all sections of this course to the course list sections
        allSections.forEach(section => {
          courseListSections.push(mapRutgersCourseToScheduledCourse(section));
        });
        scheduleDebug(`  → ✅ Added ${allSections.length} sections`);
      } else {
        stats.noSections++;
        // Log unknown courses for debugging, but skip them
        scheduleDebug(`  → ❌ No sections found - skipping`);
      }
    });

    // 🔍 DEBUG: Final summary
    scheduleDebug('🔍 [COURSE LIST DEBUG] Processing complete:', {
      stats,
      totalSections: courseListSections.length,
      uniqueBaseIdsCount: uniqueCourseBaseIds.size,
      uniqueBaseIds: Array.from(uniqueCourseBaseIds).slice(0, 20)
    });

    // Allow adding the course list even if all courses are duplicates
    // The course list can still be useful for configuration purposes
    // If no sections were found, we'll still create an empty course list structure
    if (courseListSections.length === 0) {
      scheduleDebug("⚠️ [COURSE LIST DEBUG] No valid courses found in course list after filtering duplicates, but allowing empty course list");
      // Continue to create the course list with empty sections
    }

    const sectionsByBaseId = new Map<string, ScheduledCourse[]>();
    courseListSections.forEach(section => {
      const baseId = section.baseId;
      if (!sectionsByBaseId.has(baseId)) {
        sectionsByBaseId.set(baseId, []);
      }
      sectionsByBaseId.get(baseId)!.push(section);
    });

    // 🔍 DEBUG: Final structure
    scheduleDebug('🔍 [COURSE LIST DEBUG] Final structure:', {
      sectionsByBaseIdSize: sectionsByBaseId.size,
      sectionsByBaseIdKeys: Array.from(sectionsByBaseId.keys()).slice(0, 10),
      totalSectionsInMap: Array.from(sectionsByBaseId.values()).reduce((sum, sections) => sum + sections.length, 0)
    });

    const initialAllowedCourses = restoreCourseListEligibility(courseListSections, undefined, completedCourseBaseIds);
    const defaultQuantity = 1;

    // Create a GroupedCourse for the course list
    // Even if all courses were filtered out as duplicates, we still create the course list
    // This allows users to add course lists even when all courses are already selected
    const newCourseListGrouped: GroupedCourse = {
      id: `list:${courseList.id}`, // Use a special ID format for course lists
      name: courseList.name,
      sections: courseListSections,
      selectedSection: courseListSections.length > 0 ? courseListSections[0] : undefined, // Default to first section if available
      isCourseList: true,
      courseList: courseList,
      quantity: defaultQuantity,
      allowedCourses: initialAllowedCourses
    };

    // 🔍 DEBUG: Final course list object
    scheduleDebug('🔍 [COURSE LIST DEBUG] Created GroupedCourse:', {
      id: newCourseListGrouped.id,
      name: newCourseListGrouped.name,
      sectionsCount: newCourseListGrouped.sections.length,
      quantity: newCourseListGrouped.quantity,
      allowedCoursesCount: Object.keys(newCourseListGrouped.allowedCourses ?? {}).length
    });

    const updatedSelectedCourses = [...selectedCourses, newCourseListGrouped];
    setSelectedCourses(updatedSelectedCourses);
    setHasUnsavedChanges(true);
    markAsChanged(); // Mark that user made changes
  }, [selectedCourses, allCourses, completedCourseBaseIds, markAsChanged]);

  const handleCourseListRemove = useCallback((courseList: CourseList) => {


    const updatedSelectedCourses = selectedCourses.filter(course =>
      !(course.isCourseList && course.courseList?.id === courseList.id)
    );

    setSelectedCourses(updatedSelectedCourses);
    setHasUnsavedChanges(true);
    markAsChanged(); // Mark that user made changes
  }, [selectedCourses, markAsChanged]);

  const openCourseConfig = useCallback((courseId: string, focusSectionId?: string) => {
    const course = selectedCourses.find(
      (group) => !group.isCourseList && group.id === courseId
    );
    if (!course) return;
    setShouldRegenerateAfterConfigClose(false);
    setCourseConfigState({ courseId, focusSectionId });
  }, [selectedCourses]);

  const openCourseListConfig = useCallback(
    (groupId: string, initialCourseId?: string, initialSectionId?: string) => {
      const courseListGroup = selectedCourses.find(
        (group) => group.isCourseList && group.id === groupId
      );
      if (!courseListGroup) return;
      setShouldRegenerateAfterConfigClose(false);
      setCourseListConfigState({ groupId, initialCourseId, initialSectionId });
    },
    [selectedCourses]
  );

  const closeCourseConfig = useCallback(() => {
    setCourseConfigState({ courseId: null });
    setShouldRegenerateAfterConfigClose(prev => {
      if (prev) {
        setShouldForceRegenerate(true);
      }
      return false;
    });
  }, []);

  const closeCourseListConfig = useCallback(() => {
    setCourseListConfigState({ groupId: null });
    setShouldRegenerateAfterConfigClose(prev => {
      if (prev) {
        setShouldForceRegenerate(true);
      }
      return false;
    });
  }, []);

  const handleScheduleCourseConfigRequest = useCallback((course: ScheduledCourse) => {
    const directMatch = selectedCourses.find(
      (group) => !group.isCourseList && group.id === course.baseId
    );
    if (directMatch) {
      openCourseConfig(directMatch.id, course.id);
      return;
    }

    const listMatch = selectedCourses.find(
      (group) => group.isCourseList && (group.sections || []).some(section => section.id === course.id)
    );
    if (listMatch) {
      openCourseListConfig(listMatch.id, course.baseId, course.id);
    }
  }, [selectedCourses, openCourseConfig, openCourseListConfig]);

  const handleManualCourseConfigOpen = useCallback((courseId: string) => {
    openCourseConfig(courseId);
  }, [openCourseConfig]);

  const handleManualCourseListConfigOpen = useCallback((groupId: string) => {
    openCourseListConfig(groupId);
  }, [openCourseListConfig]);

  const handleSectionChange = useCallback((courseId: string, sectionId: string) => {


    const updatedSelectedCourses = selectedCourses.map(course => {
      if (course.id === courseId) {
        const newSelectedSection = course.sections.find(section => section.id === sectionId);
        if (newSelectedSection) {
          return {
            ...course,
            selectedSection: newSelectedSection
          };
        }
      }
      return course;
    });

    setSelectedCourses(updatedSelectedCourses);
    setHasUnsavedChanges(true);
    markAsChanged(); // Mark that user made changes
  }, [selectedCourses, markAsChanged]);

  const handleQuantityChange = useCallback((courseId: string, quantity: number) => {


    const updatedSelectedCourses = selectedCourses.map(course => {
      if (course.id === courseId) {
        return {
          ...course,
          quantity: quantity
        };
      }
      return course;
    });

    setSelectedCourses(updatedSelectedCourses);
    setHasUnsavedChanges(true);
    markAsChanged(); // Mark that user made changes
  }, [selectedCourses, markAsChanged]);

  const handleAllowedSectionsChange = useCallback((courseId: string, allowedSections: string[]) => {
    const updatedSelectedCourses = selectedCourses.map(course => {
      if (course.id === courseId) {
        return {
          ...course,
          allowedSections: allowedSections
        };
      }
      return course;
    });

    setSelectedCourses(updatedSelectedCourses);
    setHasUnsavedChanges(true);
    markAsChanged();
    if (courseConfigState.courseId) {
      setShouldRegenerateAfterConfigClose(true);
    } else {
      setShouldForceRegenerate(true);
    }
  }, [selectedCourses, courseConfigState.courseId, markAsChanged]);

  const handleCourseListConfigChange = useCallback((courseId: string, quantity: number, allowedCourses: { [courseId: string]: string[] }) => {
    const updatedSelectedCourses = selectedCourses.map(course => {
      if (course.id === courseId) {
        return {
          ...course,
          quantity: quantity,
          allowedCourses: allowedCourses
        };
      }
      return course;
    });

    setSelectedCourses(updatedSelectedCourses);
    setHasUnsavedChanges(true);
    markAsChanged();
    if (courseListConfigState.groupId) {
      setShouldRegenerateAfterConfigClose(true);
    } else {
      setShouldForceRegenerate(true);
    }
  }, [selectedCourses, courseListConfigState.groupId, markAsChanged]);



  const handleScheduleSelect = (index: number) => {
    additionUndo.retire();
    setCurrentScheduleIndex(index);
    setHasUnsavedChanges(true);
    markAsChanged(); // Mark that user made changes
  };

  const copyOpenSectionIds = useMemo(() => generateOnlyOpenSections && openSectionsService.openSectionsCache.has(openSectionsTermId)
    ? [...openSectionsService.openSectionsCache.get(openSectionsTermId)!] : null,
    [generateOnlyOpenSections, openSectionsTermId, openSectionsRevision]);
  const copyPage = useScheduleCopyPageState({
    snapshot: {
      actorId: user?.id ?? null, source: schedule, fallbackTerm: openSectionsTermId,
      isViewingSelectedSchedule, generatedEligible: !generatedSaveDisabled,
      ownedDraftReady: Boolean(schedule && schedule.schedule_id === scheduleId && !isLoading
        && loadedCatalogTerm === catalogTerm && initializedScheduleKey.current === `${schedule.schedule_id}:${catalogTerm}`),
      visibleOption: generatedSchedules[currentScheduleIndex] ?? null,
      currentConfiguration: configurationDataForAutosave?.configuration ?? null,
    },
    typedName: tempName, currentScheduleIndex, generationInput,
    openSectionIds: copyOpenSectionIds,
  });
  const copyPayload = hydration.canWrite() && copyPage.canDispatch ? copyPage.intentPayload : null;
  const copyDraft = copyPage.draft;
  const scheduleCopy = useScheduleCopy({
    ownerId: user?.id ?? null,
    sourceId: schedule?.schedule_id === scheduleId ? scheduleId : null,
    sourceOwnerId: schedule?.user_id ?? null, routeKey: scheduleRouteKey,
    term: schedule?.term_id || openSectionsTermId, draft: copyDraft, payload: copyPage.intentPayload,
    canDispatch: () => hydration.canWrite() && copyPage.canDispatch,
    isRouteCurrent: () => typeof window === 'undefined' || (window.location.pathname === creationPathname
      && new URLSearchParams(window.location.search).toString() === creationQuery),
    createCopy: payload => schedulesApi.create(payload),
    onSignInRequired: () => router.push('/login'),
  });
  const handleSaveToMySchedules = scheduleCopy.copy;
  const handleCommitName = () => {
    if (!schedule) return;

    const trimmedName = tempName.trim();
    if (!trimmedName) {
      setTempName(schedule.name || '');
      setIsEditingName(false);
      return;
    }

    if (schedule.name !== trimmedName) {
      setSchedule((prev: Schedule | null) => prev ? { ...prev, name: trimmedName } : null);
      setHasUnsavedChanges(true);
      markAsChanged(); // Mark that user made changes
    }
    setIsEditingName(false);
  };

  const handleTogglePublic = () => {
    if (!schedule) return;

    const newIsPublic = !schedule.is_public;
    setSchedule((prev: Schedule | null) => prev ? { ...prev, is_public: newIsPublic } : null);
    setHasUnsavedChanges(true);
    markAsChanged(); // Mark that user made changes
  };

  const directWriteDraft = useMemo(() => ({
    selectedSaveDraft, signature: currentGenerationSignature, resultIdentity: generationIdentity,
    options: generatedSchedules, optionIndex: currentScheduleIndex, isViewingSelectedSchedule,
  }), [selectedSaveDraft, currentGenerationSignature, generationIdentity, generatedSchedules, currentScheduleIndex, isViewingSelectedSchedule]);
  const directWrite = useScheduleWriteAuthority({
    ownerId: user?.id ?? null,
    scheduleId: schedule?.user_id === user?.id && schedule?.schedule_id === scheduleId ? scheduleId : null,
    routeKey: scheduleRouteKey, term: catalogTerm, draft: directWriteDraft,
  });
  const generatedSaveOutcome = useScheduleSaveOutcome({
    ownerId: user?.id ?? null, scheduleId: schedule?.schedule_id ?? null,
    draft: directWriteDraft,
    snapshot: { name: schedule?.name ?? '', classes: schedule?.classes ?? [], configuration: schedule?.configuration ?? null },
    isSelected: isViewingSelectedSchedule, isDirty: hasUnsavedChanges,
  });

  // Save and Make Primary share one retained slot through the entire chain.
  const saveCurrentScheduleDraft = async (scheduleIndex: number, makePrimary: boolean): Promise<boolean> => {
    if (!hydration.canWrite() || !schedule || !user || (!isViewingSelectedSchedule && (generatedSaveDisabled || !generatedSchedules[scheduleIndex]))) return false;
    const operation = directWrite.begin();
    if (!operation) return false;
    ownDirectSaveOperation.current = operation;
    const generated = !isViewingSelectedSchedule;
    const receipt = generatedSaveOutcome.begin(operation, directWrite.captureContext(), {
      scheduleName: schedule.name, termLabel: SEMESTERS.find(term => term.id === catalogTerm)?.label ?? catalogTerm,
      candidate: generated ? scheduleIndex + 1 : null, makePrimary,
    });
    if (!receipt) { ownDirectSaveOperation.current = null; operation.finish(); return false; }
    try {
      const option = generated ? generatedSchedules[scheduleIndex] : null;
      const classes = option ? option.courses.map(course => `${course.baseId}:${course.id}`) : [...(schedule.classes ?? [])];
      const courses = generated ? generationIdentity!.input.groupedCourses : selectedCourses;
      const configuration: ScheduleConfiguration = {
        selectedCourses: courses.map(course => ({
          id: course.id, name: course.name, selectedSectionId: course.selectedSection?.id,
          isCourseList: course.isCourseList, courseListId: course.courseList?.id,
          quantity: course.quantity, allowedSections: course.allowedSections, allowedCourses: course.allowedCourses,
        })),
        pinnedClasses, currentScheduleIndex: 0,
        selectedCourseIds: option ? option.courses.map(course => course.id) : selectedCourses.map(course => course.id),
        customActivities,
      };
      return await persistScheduleDraft(operation, { scheduleId: schedule.schedule_id,
        updates: { classes, configuration }, makePrimary }, {
        writeSchedule: (id, updates) => schedulesApi.update(id, updates),
        writePrimary: id => usersApi.update({ p_primary_schedule_id: id }),
        publish: (identity, updates, primary, currentDestination) => {
          // The async helper has acknowledged the complete chain, including primary setup.
          // One local commit publishes the selected record/view/clean state and its named receipt.
          commitScheduleSavePublication(() => {
            setSchedule(previous => currentDestination() && previous?.user_id === identity.ownerId && previous.schedule_id === identity.scheduleId
              ? { ...previous, classes: [...(updates.classes ?? [])], configuration: updates.configuration } : previous);
            if (primary) setPrimaryScheduleId(previous => currentDestination() ? identity.scheduleId : previous);
            if (generated) {
              setIsViewingSelectedSchedule(previous => currentDestination() ? true : previous);
              setCurrentScheduleIndex(previous => currentDestination() ? 0 : previous);
            }
            setHasUnsavedChanges(previous => currentDestination() ? false : previous);
            const savedAt = new Date();
            setLastSaved(previous => currentDestination() ? savedAt : previous);
            receipt.succeed({ name: schedule.name, classes: [...(updates.classes ?? [])], configuration: updates.configuration }, savedAt, currentDestination);
            trackScheduleSaved(classes.length, generated ? 'generated_schedule' : 'selected_schedule');
          });
        },
        onError: (error, currentReceipt) => {
          console.error('Failed to save schedule:', error);
          receipt.fail(currentReceipt);
        },
      });
    } finally { receipt.finish(); if (ownDirectSaveOperation.current === operation) ownDirectSaveOperation.current = null; operation.finish(); }
  };
  const handleSaveGeneratedSchedule = (scheduleIndex: number) => saveCurrentScheduleDraft(scheduleIndex, false);

  const handleGenerateRandomSchedule = () => {
    const pool = allScoredSchedules.length > 0 ? allScoredSchedules : generatedSchedules;
    if (pool.length > 0) {
      additionUndo.retire();
      const randomIndex = Math.floor(Math.random() * pool.length);
      const randomSchedule = pool[randomIndex];
      // Prepend random schedule to the limited list, dedup by joined course IDs
      const key = (s: GeneratedSchedule) => s.courses.map(c => c.id).sort().join('|');
      const seen = new Set<string>();
      const combined = [randomSchedule, ...generatedSchedules]
        .filter(s => {
          const k = key(s);
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        })
        .slice(0, SCHEDULE_DISPLAY_LIMITS.MAX_SCHEDULES_TO_DISPLAY);
      const changed = isViewingSelectedSchedule || currentScheduleIndex !== 0
        || combined.length !== generatedSchedules.length
        || combined.some((entry, index) => key(entry) !== key(generatedSchedules[index]));
      setGeneratedSchedules(combined);
      setCurrentScheduleIndex(0);
      setIsViewingSelectedSchedule(false);
      if (changed) { setHasUnsavedChanges(true); markAsChanged(); }
    }
  };

  const handleCustomActivityAdd = useCallback((activity: Omit<CustomActivity, 'id'>) => {
    const nextActivity: CustomActivity = {
      ...activity,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
    setCustomActivities(prev => [...prev, nextActivity]);
    setHasUnsavedChanges(true);
    markAsChanged();
  }, [markAsChanged]);

  const handleCustomActivityRemove = useCallback((activityId: string) => {
    setCustomActivities(prev => prev.filter(activity => activity.id !== activityId));
    setHasUnsavedChanges(true);
    markAsChanged();
  }, [markAsChanged]);

  const handleTimeBlockSelect = useCallback((selection: { day: string; startMinute: number; endMinute: number }) => {
    setCustomActivityDraft({
      ...selection,
      updatedAt: Date.now(),
    });
  }, []);

  const handleFindClassesInTimeBlock = useCallback((selection: { day: string; startMinute: number; endMinute: number }) => {
    const params = new URLSearchParams({
      day: selection.day,
      start: String(selection.startMinute),
      end: String(selection.endMinute),
    });
    router.push(`/classes-by-time?${params.toString()}`);
  }, [router]);

  const handleMakeTimeBlockAvailable = useCallback((selection: { day: string; startMinute: number; endMinute: number }) => {
    const dayIndex = AVAILABILITY_DAYS.indexOf(selection.day as typeof AVAILABILITY_DAYS[number]);
    if (dayIndex < 0 || !preferences) return;

    const nextAvailability = Array.from({ length: AVAILABILITY_DAYS.length }, (_, index) => {
      const existing = preferences.availability?.[index];
      return Array.isArray(existing)
        ? [...existing]
        : Array(AVAILABILITY_SLOT_COUNT).fill(true);
    });

    const startSlot = Math.max(0, getAvailabilitySlotIndex(selection.startMinute));
    const endSlot = Math.min(AVAILABILITY_SLOT_COUNT - 1, getAvailabilitySlotIndex(selection.endMinute - 1));
    for (let slot = startSlot; slot <= endSlot; slot += 1) {
      nextAvailability[dayIndex][slot] = true;
    }

    handlePreferencesChange({ ...preferences, availability: nextAvailability });
  }, [handlePreferencesChange, preferences]);

  const handleMakePrimary = async (scheduleIndex: number) => {
    await saveCurrentScheduleDraft(scheduleIndex, true);
  };

  // Export functions
  const scheduleViewRef = useRef<HTMLDivElement>(null);
  const scheduleTitleRef = useRef<HTMLHeadingElement>(null);
  const initialRecoveryFocusRef = useRef<HTMLDivElement>(null);
  const renderWorkspaceFocusScope = (content: React.ReactNode) => (
    <div ref={initialRecoveryFocusRef} role="region" aria-label="Scheduling workspace" tabIndex={-1} className="h-full min-h-0">
      {content}
    </div>
  );
  const regenerationIndicatorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleExportToImage = async () => {
    try {
      // Create a temporary div for the export
      const exportDiv = document.createElement('div');
      exportDiv.style.position = 'absolute';
      exportDiv.style.left = '-9999px';
      exportDiv.style.top = '-9999px';
      document.body.appendChild(exportDiv);

      // Create a temporary ScheduleView component for export
      const tempScheduleView = document.createElement('div');
      exportDiv.appendChild(tempScheduleView);

      // Use ReactDOM to render the export version
      const { createRoot } = require('react-dom/client');
      const ExportScheduleView = React.createElement(ScheduleView, {
        schedule: isViewingSelectedSchedule && schedule?.classes
          ? schedule.classes.map(classId => {
            const [baseId, sectionId] = classId.split(':');
            const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
            return course ? mapRutgersCourseToScheduledCourse(course) : null;
          }).filter(Boolean) as ScheduledCourse[]
          : generatedSchedules[currentScheduleIndex]?.courses || [],
        onNextSchedule: () => { },
        onPrevSchedule: () => { },
        currentScheduleIndex: isViewingSelectedSchedule ? -1 : currentScheduleIndex,
        totalSchedules: schedule?.user_id === user?.id ? Math.max(generatedSchedules.length, 1) : 1,
        totalSchedulesGenerated: totalSchedulesGenerated,
        allSectionsByCourseId: Object.fromEntries(selectedCourses.map(course => [course.id, course.sections])),
        friendPresenceBySection: friendPresenceBySection,
        conflicts: overlapConflicts,
        exportMode: true, // This will render only the grid
      });

      // Use createRoot instead of render for React 18+
      const root = createRoot(tempScheduleView);
      root.render(ExportScheduleView);

      // Wait for the component to render, then capture
      setTimeout(async () => {
        try {
          const [{ default: html2canvas }, { saveAs }] = await Promise.all([
            import('html2canvas'),
            import('file-saver'),
          ]);
          const canvas = await html2canvas(tempScheduleView, {
            useCORS: true,
            allowTaint: true,
            logging: false,
          });

          // Convert canvas to blob
          canvas.toBlob(async (blob) => {
            if (blob) {
              // Save the file
              saveAs(blob, 'schedule.png');

              // Copy to clipboard
              try {
                // Create a ClipboardItem for the image
                const clipboardItem = new ClipboardItem({
                  'image/png': blob
                });
                await navigator.clipboard.write([clipboardItem]);
                showCustomNotification('Schedule exported and copied to clipboard!', 'success');
              } catch (clipboardError) {
                console.warn('Clipboard copy failed, but file was saved:', clipboardError);
                showCustomNotification('Schedule exported! (Clipboard copy not supported)', 'success');
              }
            }

            // Clean up
            root.unmount();
            document.body.removeChild(exportDiv);
          }, 'image/png', 0.95);
        } catch (error) {
          console.error('Export failed:', error);
          showCustomNotification('Export failed. Please try again.', 'error');
          // Clean up on error
          root.unmount();
          document.body.removeChild(exportDiv);
        }
      }, 100);
    } catch (error) {
      console.error('Export setup failed:', error);
      showCustomNotification('Export failed. Please try again.', 'error');
    }
  };

  const handleCopyScheduleLink = async () => {
    if (!schedule) return;

    if (!schedule.is_public) {
      showToast('Make this schedule public before copying its link.', 'info');
      return;
    }

    const scheduleUrl = `${window.location.origin}/scheduling?id=${schedule.schedule_id}`;
    try {
      await navigator.clipboard.writeText(scheduleUrl);
      showToast('Public schedule link copied.', 'success');
    } catch {
      showToast('Could not copy the schedule link.', 'error');
    }
  };

  const handleCopyIcsSubscribeLink = async () => {
    if (!hydration.canWrite() || !schedule) return;
    const operation = directWrite.begin();
    if (!operation) return;
    try {
      await shareScheduleCalendar(operation, schedule.is_public, window.location.origin, {
        writePublic: id => schedulesApi.update(id, { is_public: true }),
        copy: url => navigator.clipboard.writeText(url),
        publishPublic: (identity, currentDestination) => setSchedule(previous => currentDestination() && previous?.user_id === identity.ownerId && previous.schedule_id === identity.scheduleId
          ? { ...previous, is_public: true } : previous),
        onCopied: madePublic => {
          if (madePublic) showCustomNotification('Schedule made public for calendar sharing', 'success');
          showCustomNotification('Calendar subscribe link copied (ICS). Use in Google Calendar → From URL.', 'success');
        },
        onError: error => {
          console.error('Failed to make schedule public:', error);
          showCustomNotification('Failed to make schedule public for calendar sharing', 'error');
        },
      });
    } finally { operation.finish(); }
  };

  const handleExportToWebReg = () => {
    if (!schedule) return;

    // Get current courses from the schedule
    const currentCourses = isViewingSelectedSchedule && schedule?.classes
      ? schedule.classes.map(classId => {
        const [baseId, sectionId] = classId.split(':');
        const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
        return course ? mapRutgersCourseToScheduledCourse(course) : null;
      }).filter(Boolean) as ScheduledCourse[]
      : generatedSchedules[currentScheduleIndex]?.courses || [];

    // Extract section numbers for WebReg
    const sectionNumbers = currentCourses.map(course => {
      // Try to extract section number from the course ID first
      const sectionMatch = course.id.match(/\d+$/);
      if (sectionMatch) {
        return sectionMatch[0];
      }

      // Fallback to section field
      const sectionNumberMatch = course.section.match(/\d+/);
      if (sectionNumberMatch) {
        return sectionNumberMatch[0];
      }

      // If no section number found, try to extract from the full course ID
      const fullIdMatch = course.id.match(/\d+$/);
      return fullIdMatch ? fullIdMatch[0] : 'unknown';
    }).join(',');

    // Create WebReg URL with correct semester
    // Determine the correct semester code based on term_id
    const termId = schedule?.term_id || activeSemester;
    const semesterSelection = webRegSemesterSelection(termId);
    if (!semesterSelection) {
      showToast('WebReg is unavailable for this schedule’s term.', 'error');
      return;
    }
    const webRegUrl = `https://sims.rutgers.edu/webreg/editSchedule.htm?login=cas&semesterSelection=${semesterSelection}&indexList=${sectionNumbers}`;

    // Open in new tab
    window.open(webRegUrl, '_blank');
  };

  const handleExportToGoogleCalendar = async () => {
    if (!schedule) return;

    // Get current courses from the schedule
    const currentCourses = isViewingSelectedSchedule && schedule?.classes
      ? schedule.classes.map(classId => {
        const [baseId, sectionId] = classId.split(':');
        const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
        return course ? mapRutgersCourseToScheduledCourse(course) : null;
      }).filter(Boolean) as ScheduledCourse[]
      : generatedSchedules[currentScheduleIndex]?.courses || [];

    try {
      const result = await exportToGoogleCalendar(currentCourses, schedule.name);

      // Show notification instead of alert
      if (result.success) {
        const verifiedHandoff = user?.id && optimalSetupCalendarHandoff?.ownerUserId === user.id
          ? optimalSetupCalendarHandoff
          : null;
        if (verifiedHandoff && !optimalSetupCalendarVerifiedRef.current) {
          optimalSetupCalendarVerifiedRef.current = true;
          recordVerifiedOptimalSetupUse(window.localStorage, verifiedHandoff.ownerUserId, 'calendar');
          trackEvent('optimal_setup:handoff', {
            entrypoint: verifiedHandoff.entrypoint,
            item: 'calendar',
            stage: 'verified_downstream_use',
          });
        }
        // You can implement a toast notification system here
        // For now, using a simple notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Export Complete', {
            body: result.message,
            icon: '/favicon.ico'
          });
        } else {
          // Fallback to a custom notification div
          showCustomNotification(result.message, 'success');
        }

        // Open Google Calendar in a new tab after successful export
        window.open('https://calendar.google.com/calendar/u/0/r', '_blank');
      } else {
        showCustomNotification(result.message, 'error');
      }
    } catch (error) {
      showCustomNotification('Failed to export to Google Calendar', 'error');
    }
  };

  // Custom notification function
  const showCustomNotification = (message: string, type: 'success' | 'error') => {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 z-50 px-6 py-4 rounded-control shadow-lg text-white font-medium ${type === 'success' ? 'bg-green-600' : 'bg-red-600'
      }`;
    notification.textContent = message;

    // Add to page
    document.body.appendChild(notification);

    // Remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  };

  // Warning system functions
  const addWarning = (warning: Omit<Warning, 'id' | 'timestamp'>) => {
    const newWarning: Warning = {
      ...warning,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date()
    };
    setWarnings(prev => [...prev, newWarning]);
  };

  const dismissWarning = (warningId: string) => {
    setWarnings(prev => prev.filter(w => w.id !== warningId));
  };

  const dismissAllWarnings = () => {
    setWarnings([]);
  };

  // Warning checking functions
  const checkScheduleWarnings = () => {
    if (!isViewingSelectedSchedule && !generatedResultsCurrent) { setWarnings([]); return; }
    const currentWarnings: Omit<Warning, 'id' | 'timestamp'>[] = [];

    // Get current schedule courses
    const currentScheduleCourses = isViewingSelectedSchedule && schedule?.classes
      ? schedule.classes.map(classId => {
        const [baseId, sectionId] = classId.split(':');
        const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
        return course ? mapRutgersCourseToScheduledCourse(course) : null;
      }).filter(Boolean) as ScheduledCourse[]
      : generatedSchedules[currentScheduleIndex]?.courses || [];

    // Check if number of classes doesn't match expected
    const expectedCourseCount = selectedCourses.reduce((total, course) => {
      if (course.isCourseList) {
        return total + (course.quantity || 1);
      }
      return total + 1;
    }, 0);

    if (currentScheduleCourses.length !== expectedCourseCount) {
      currentWarnings.push({
        type: 'warning',
        title: 'Schedule Incomplete',
        message: `Expected ${expectedCourseCount} courses but found ${currentScheduleCourses.length}. Some courses may not have been scheduled.`,
        dismissible: true
      });
    }

    // Check if any classes are closed
    const closedSections = currentScheduleCourses.filter(course => {
      const sectionId = course.id.split(':').pop() || '';
      // Only check if we have a valid section ID
      if (!sectionId) return false;
      return !openSectionsService.isOpen(sectionId, openSectionsTermId);
    });

    if (closedSections.length > 0) {
      currentWarnings.push({
        type: 'warning',
        title: 'Closed sections',
        message: `${closedSections.length} section(s) are currently closed. You may need to wait for openings or choose alternative sections.`,
        dismissible: true
      });
    }

    // Debug warning to help identify when status data is available
    // This can be removed once we have proper status data integration
    /* const coursesWithStatus = currentScheduleCourses.filter(course => course.status);
    if (coursesWithStatus.length > 0 && coursesWithStatus.length < currentScheduleCourses.length) {
      currentWarnings.push({
        type: 'info',
        title: 'Status Data Available',
        message: `${coursesWithStatus.length} out of ${currentScheduleCourses.length} courses have status information. Consider updating the status mapping for all courses.`,
        dismissible: true
      });
    } */

    // Check for time conflicts
    const timeConflicts = findTimeConflicts(currentScheduleCourses);
    const overlapConflicts = findOverlapConflicts(currentScheduleCourses);

    // Update overlap conflicts state for the modal
    setOverlapConflicts(overlapConflicts);

    if (timeConflicts.length > 0) {
      currentWarnings.push({
        type: 'error',
        title: 'Time Conflicts Detected',
        message: `Found ${timeConflicts.length} time conflict(s) in your schedule. Click the conflict indicator to resolve them.`,
        dismissible: true
      });
    }

    // Check for campus distribution
    const campusCounts = currentScheduleCourses.reduce((acc, course) => {
      acc[course.campus] = (acc[course.campus] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const campusWarnings = Object.entries(campusCounts).filter(([campus, count]) => count > 3);
    if (campusWarnings.length > 0) {
      currentWarnings.push({
        type: 'info',
        title: 'Campus Distribution',
        message: `You have ${campusWarnings.map(([campus, count]) => `${count} classes on ${campus}`).join(', ')}. Consider spreading classes across campuses.`,
        dismissible: true
      });
    }

    // Check for early/late classes
    const earlyClasses = currentScheduleCourses.filter(course =>
      course.meetingTimes?.some(mt =>
        mt.startMinute && mt.startMinute > 0 && mt.startMinute < 8 * 60
      ) // Before 8 AM, but exclude TBA/async classes
    );
    const lateClasses = currentScheduleCourses.filter(course =>
      course.meetingTimes?.some(mt =>
        mt.startMinute && mt.startMinute > 0 && mt.startMinute > 18 * 60
      ) // After 6 PM, but exclude TBA/async classes
    );

    if (earlyClasses.length > 0) {
      currentWarnings.push({
        type: 'info',
        title: 'Early Classes',
        message: `You have ${earlyClasses.length} early class(es) (before 8 AM). Make sure this works with your schedule.`,
        dismissible: true
      });
    }

    if (lateClasses.length > 0) {
      currentWarnings.push({
        type: 'info',
        title: 'Late Classes',
        message: `You have ${lateClasses.length} late class(es) (after 6 PM). Consider transportation and safety.`,
        dismissible: true
      });
    }

    // Check for back-to-back classes
    const backToBackClasses = findBackToBackClasses(currentScheduleCourses);
    if (backToBackClasses.length > 0) {
      currentWarnings.push({
        type: 'warning',
        title: 'Back-to-Back Classes',
        message: `You have ${backToBackClasses.length} back-to-back class(es). Consider travel time between campuses.`,
        dismissible: true
      });
    }

    // Check for classes on different campuses that are too close in time
    const crossCampusWarnings = findCrossCampusWarnings(currentScheduleCourses);
    if (crossCampusWarnings.length > 0) {
      currentWarnings.push({
        type: 'warning',
        title: 'Cross-Campus Travel',
        message: `You have ${crossCampusWarnings.length} class(es) that require cross-campus travel. Plan accordingly.`,
        dismissible: true
      });
    }

    // Check for heavy course load
    const totalCredits = currentScheduleCourses.reduce((total, course) => total + (Number(course.credits) || 0), 0);
    if (totalCredits > 18) {
      currentWarnings.push({
        type: 'warning',
        title: 'Heavy Course Load',
        message: `You're taking ${totalCredits} credits. Consider if this workload is manageable.`,
        dismissible: true
      });
    } else if (totalCredits < 12) {
      currentWarnings.push({
        type: 'info',
        title: 'Light Course Load',
        message: `You're taking ${totalCredits} credits. Make sure this meets your degree requirements.`,
        dismissible: true
      });
    }

    // Check for missing core requirements
    const selectedCoreRequirements = new Set<string>();
    currentScheduleCourses.forEach(course => {
      if (course.coreRequirements) {
        course.coreRequirements.forEach(req => selectedCoreRequirements.add(req));
      }
    });

    // This would need to be implemented based on your degree requirements
    // For now, we'll just show a placeholder
    if (selectedCoreRequirements.size === 0) {
      currentWarnings.push({
        type: 'info',
        title: 'Core Requirements',
        message: 'No core requirements detected. Make sure to check your degree requirements.',
        dismissible: true
      });
    }

    // Friends: same course as you
    if (currentScheduleCourses.some(c => friendsSameCourseBaseIds.has(c.baseId))) {
      currentWarnings.push({
        type: 'info',
        title: 'Friends in the same course',
        message: 'One or more friends are taking the same course as you.',
        dismissible: true,
      });
    }

    // Friends: friend course appears in your course lists
    if (myCourseLists && myCourseLists.length > 0 && friendsWithSchedules.length > 0) {
      const myListBaseIds = new Set<string>();
      myCourseLists.forEach(list => {
        (list.classes || []).forEach(code => {
          const normalized = (code || '').replace(/[^0-9]/g, '');
          if (normalized) myListBaseIds.add(normalized);
        });
      });
      let friendInList = false;
      friendsWithSchedules.forEach(f => {
        (f.primarySchedule?.classes || []).forEach((entry: string) => {
          const [baseId] = (entry || '').split(':');
          if (baseId && myListBaseIds.has(baseId)) friendInList = true;
        });
      });
      if (friendInList) {
        currentWarnings.push({
          type: 'info',
          title: 'Friend Course in Your Lists',
          message: 'A class your friend is in appears in one of your course lists.',
          dismissible: true,
        });
      }
    }

    // Update warnings with proper IDs and timestamps
    setWarnings(currentWarnings.map(warning => ({
      ...warning,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date()
    })));
  };

  // Helper function to find time conflicts
  const findTimeConflicts = (courses: ScheduledCourse[]): string[] => {
    const conflicts: string[] = [];

    for (let i = 0; i < courses.length; i++) {
      for (let j = i + 1; j < courses.length; j++) {
        const course1 = courses[i];
        const course2 = courses[j];

        if (!course1.meetingTimes || !course2.meetingTimes) continue;

        for (const meeting1 of course1.meetingTimes) {
          for (const meeting2 of course2.meetingTimes) {
            if (meeting1.day === meeting2.day) {
              // Check for time overlap
              const start1 = meeting1.startMinute;
              const end1 = meeting1.endMinute;
              const start2 = meeting2.startMinute;
              const end2 = meeting2.endMinute;

              // Skip if any time is invalid (TBA/async classes)
              if (!start1 || !end1 || !start2 || !end2 ||
                start1 <= 0 || end1 <= 0 || start2 <= 0 || end2 <= 0) continue;

              if ((start1 < end2 && end1 > start2)) {
                conflicts.push(`${course1.name} conflicts with ${course2.name} on ${meeting1.day}`);
              }
            }
          }
        }
      }
    }

    return conflicts;
  };

  // Enhanced function to find overlap conflicts with detailed information
  const findOverlapConflicts = (courses: ScheduledCourse[]): Array<{
    course1: ScheduledCourse;
    course2: ScheduledCourse;
    day: string;
    timeConflict: string;
  }> => {
    const conflicts: Array<{
      course1: ScheduledCourse;
      course2: ScheduledCourse;
      day: string;
      timeConflict: string;
    }> = [];

    for (let i = 0; i < courses.length; i++) {
      for (let j = i + 1; j < courses.length; j++) {
        const course1 = courses[i];
        const course2 = courses[j];

        if (!course1.meetingTimes || !course2.meetingTimes) continue;

        for (const meeting1 of course1.meetingTimes) {
          for (const meeting2 of course2.meetingTimes) {
            if (meeting1.day === meeting2.day) {
              // Check for time overlap
              const start1 = meeting1.startMinute;
              const end1 = meeting1.endMinute;
              const start2 = meeting2.startMinute;
              const end2 = meeting2.endMinute;

              // Skip if any time is invalid (TBA/async classes)
              if (!start1 || !end1 || !start2 || !end2 ||
                start1 <= 0 || end1 <= 0 || start2 <= 0 || end2 <= 0) continue;

              if ((start1 < end2 && end1 > start2)) {
                const formatTime = (minutes: number): string => {
                  const hours = Math.floor(minutes / 60);
                  const mins = minutes % 60;
                  const period = hours >= 12 ? 'PM' : 'AM';
                  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
                  return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
                };

                conflicts.push({
                  course1,
                  course2,
                  day: meeting1.day,
                  timeConflict: `${course1.name} (${formatTime(start1)}-${formatTime(end1)}) conflicts with ${course2.name} (${formatTime(start2)}-${formatTime(end2)})`
                });
              }
            }
          }
        }
      }
    }

    return conflicts;
  };

  // Helper function to find back-to-back classes
  const findBackToBackClasses = (courses: ScheduledCourse[]): string[] => {
    const backToBack: string[] = [];

    for (let i = 0; i < courses.length; i++) {
      for (let j = i + 1; j < courses.length; j++) {
        const course1 = courses[i];
        const course2 = courses[j];

        if (!course1.meetingTimes || !course2.meetingTimes) continue;

        for (const meeting1 of course1.meetingTimes) {
          for (const meeting2 of course2.meetingTimes) {
            if (meeting1.day === meeting2.day) {
              // Check for back-to-back (15 minutes or less between classes)
              const end1 = meeting1.endMinute;
              const start2 = meeting2.startMinute;

              // Skip if either time is invalid (TBA/async classes)
              if (!end1 || !start2 || end1 <= 0 || start2 <= 0) continue;

              const timeDiff = start2 - end1;

              if (timeDiff >= 0 && timeDiff <= 15) {
                backToBack.push(`${course1.name} → ${course2.name} on ${meeting1.day}`);
              }
            }
          }
        }
      }
    }

    return backToBack;
  };

  // Helper function to find cross-campus travel warnings
  const findCrossCampusWarnings = (courses: ScheduledCourse[]): string[] => {
    const crossCampus: string[] = [];

    for (let i = 0; i < courses.length; i++) {
      for (let j = i + 1; j < courses.length; j++) {
        const course1 = courses[i];
        const course2 = courses[j];

        if (!course1.meetingTimes || !course2.meetingTimes) continue;
        if (course1.campus === course2.campus) continue; // Same campus, no issue

        for (const meeting1 of course1.meetingTimes) {
          for (const meeting2 of course2.meetingTimes) {
            if (meeting1.day === meeting2.day) {
              // Check for classes on different campuses that are close in time
              const end1 = meeting1.endMinute;
              const start2 = meeting2.startMinute;

              // Skip if either time is invalid (TBA/async classes)
              if (!end1 || !start2 || end1 <= 0 || start2 <= 0) continue;

              const timeDiff = start2 - end1;

              if (timeDiff >= 0 && timeDiff <= 30) { // 30 minutes or less between campuses
                crossCampus.push(`${course1.name} (${course1.campus}) → ${course2.name} (${course2.campus}) on ${meeting1.day}`);
              }
            }
          }
        }
      }
    }

    return crossCampus;
  };



  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('leftPanelWidth');
      return saved ? parseInt(saved) : 400;
    }
    return 400;
  });

  const [isDragging, setIsDragging] = useState<'left' | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Save panel widths to localStorage
  useEffect(() => {
    localStorage.setItem('leftPanelWidth', leftPanelWidth.toString());
  }, [leftPanelWidth]);

  // Resize handlers
  const handleMouseDown = (side: 'left') => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(side);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const mouseX = e.clientX - containerRect.left;

    if (isDragging === 'left') {
      const newLeftWidth = Math.max(300, Math.min(600, mouseX));
      setLeftPanelWidth(newLeftWidth);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(null);
  };

  // Add mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  // Resize handle component
  const ResizeHandle = ({ side }: { side: 'left' }) => {
    const handleDoubleClick = () => {
      if (side === 'left') {
        setLeftPanelWidth(400); // Reset to default
      }
    };

    return (
      <div
        className={`relative h-full w-px shrink-0 cursor-col-resize bg-[var(--ss-border)] transition-colors hover:bg-[var(--ss-focus)] group ${isDragging === side ? 'bg-[var(--ss-focus)]' : ''
          }`}
        onMouseDown={handleMouseDown(side)}
        onDoubleClick={handleDoubleClick}
        title={`Resize ${side} panel (double-click to reset)`}
      >
        {/* Hover indicator */}
        <div className="absolute inset-y-0 -left-2 -right-2 bg-[var(--ss-focus)] opacity-0 transition-opacity group-hover:opacity-20" />

        {/* Resize grip dots */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex flex-col space-y-1">
            <div className="w-0.5 h-0.5 bg-gray-500 dark:bg-gray-400 rounded-full"></div>
            <div className="w-0.5 h-0.5 bg-gray-500 dark:bg-gray-400 rounded-full"></div>
            <div className="w-0.5 h-0.5 bg-gray-500 dark:bg-gray-400 rounded-full"></div>
          </div>
        </div>
      </div>
    );
  };

  // Check for warnings when schedule changes
  useEffect(() => {
    if (schedule || generatedSchedules.length > 0) {
      checkScheduleWarnings();
    }
  }, [schedule, generatedSchedules, currentScheduleIndex, isViewingSelectedSchedule, selectedCourses, generatedResultsCurrent]);





  {/* Overlap Resolution Modal */ }
  <OverlapResolutionModal
    isOpen={showOverlapModal}
    onClose={() => setShowOverlapModal(false)}
    conflicts={overlapConflicts}
    allSectionsByCourseId={Object.fromEntries(selectedCourses.map(course => [course.selectedSection?.id || course.id, course.sections]))}
    onResolveConflict={(conflictIndex, resolution) => {
      const conflict = overlapConflicts[conflictIndex];
      if (!conflict) return;

      // Get current schedule courses
      const currentScheduleCourses = isViewingSelectedSchedule && schedule?.classes
        ? schedule.classes.map(classId => {
          const [baseId, sectionId] = classId.split(':');
          const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
          return course ? mapRutgersCourseToScheduledCourse(course) : null;
        }).filter(Boolean) as ScheduledCourse[]
        : generatedSchedules[currentScheduleIndex]?.courses || [];

      // Remove courses based on resolution
      let updatedCourses = [...currentScheduleCourses];
      if (resolution === 'course1') {
        updatedCourses = updatedCourses.filter(course => course.id !== conflict.course2.id);
      } else if (resolution === 'course2') {
        updatedCourses = updatedCourses.filter(course => course.id !== conflict.course1.id);
      } else if (resolution === 'both') {
        updatedCourses = updatedCourses.filter(course =>
          course.id !== conflict.course1.id && course.id !== conflict.course2.id
        );
      }

      // Update the schedule
      if (isViewingSelectedSchedule && schedule?.classes) {
        // Update saved schedule
        const updatedClassIds = updatedCourses.map(course => course.id);
        setSchedule(prev => prev ? { ...prev, classes: updatedClassIds } : null);
      } else {
        // Update generated schedule
        const updatedGeneratedSchedules = [...generatedSchedules];
        if (updatedGeneratedSchedules[currentScheduleIndex]) {
          updatedGeneratedSchedules[currentScheduleIndex] = {
            ...updatedGeneratedSchedules[currentScheduleIndex],
            courses: updatedCourses
          };
          setGeneratedSchedules(updatedGeneratedSchedules);
        }
      }

      // This is human intent, not automatic catalog enrichment.
      setHasUnsavedChanges(true);
      markAsChanged();

      // Re-check warnings after resolution
      setTimeout(() => checkScheduleWarnings(), 100);
    }}
    onReplaceSection={(courseId, newSectionId) => {
      // Find the course to replace
      const courseToReplace = allCourses.find(c => c.id === courseId);
      if (!courseToReplace) return;

      // Get current schedule courses
      const currentScheduleCourses = isViewingSelectedSchedule && schedule?.classes
        ? schedule.classes.map(classId => {
          const [baseId, sectionId] = classId.split(':');
          const course = allCourses.find(c => c.baseId === baseId && c.id === sectionId);
          return course ? mapRutgersCourseToScheduledCourse(course) : null;
        }).filter(Boolean) as ScheduledCourse[]
        : generatedSchedules[currentScheduleIndex]?.courses || [];

      // Replace the course
      const updatedCourses = currentScheduleCourses.map(course =>
        course.id === courseId ? mapRutgersCourseToScheduledCourse(allCourses.find(c => c.id === newSectionId)!) : course
      );

      // Update the schedule
      if (isViewingSelectedSchedule && schedule?.classes) {
        // Update saved schedule
        const updatedClassIds = updatedCourses.map(course => course.id);
        setSchedule(prev => prev ? { ...prev, classes: updatedClassIds } : null);
      } else {
        // Update generated schedule
        const updatedGeneratedSchedules = [...generatedSchedules];
        if (updatedGeneratedSchedules[currentScheduleIndex]) {
          updatedGeneratedSchedules[currentScheduleIndex] = {
            ...updatedGeneratedSchedules[currentScheduleIndex],
            courses: updatedCourses
          };
          setGeneratedSchedules(updatedGeneratedSchedules);
        }
      }

      // This is human intent, not automatic catalog enrichment.
      setHasUnsavedChanges(true);
      markAsChanged();

      // Re-check warnings after replacement
      setTimeout(() => checkScheduleWarnings(), 100);
    }}
  />

  // Keyboard shortcuts for panel resizing


  if (authLoading || isLoading) {
    return renderWorkspaceFocusScope(<PageLoading title="Preparing your schedule" description="Restoring courses, preferences, and your weekly plan." />);
  }

  if ((hydration.phase === 'error' || hydration.phase === 'conflict') && !(schedule?.user_id === user?.id && schedule?.schedule_id === scheduleId
    && initializedScheduleKey.current === `${schedule.schedule_id}:${schedule.term_id}`)) {
    return renderWorkspaceFocusScope(<div className="p-6"><OwnedHydrationRecovery returnFocusRef={initialRecoveryFocusRef} phase={hydration.phase} targetTerm={hydration.targetTerm} error={hydration.error} onUseSaved={() => { void hydration.load('review'); }} /></div>);
  }

  if (error) {
    return renderWorkspaceFocusScope(
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center bg-red-100 dark:bg-red-500/10 border border-red-300 dark:border-red-500/20 rounded-control p-8 max-w-md">
          <XMarkIcon className="h-16 w-16 text-red-500 dark:text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">Error</h2>
          <p className="text-red-700 dark:text-red-300">{error}</p>
          <button
            onClick={() => router.push('/scheduling')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-control hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!schedule && !showScheduleModal) {
    return renderWorkspaceFocusScope(<div className="flex min-h-full items-center justify-center bg-surface p-6 text-content">
      <div className="space-y-4 text-center">
        <h1 className="text-xl font-semibold">Your schedule</h1>
        <Button onClick={() => { setIsModalOpenedManually(true); setShowScheduleModal(true); }}>Choose schedule</Button>
      </div>
    </div>);
  }

  return renderWorkspaceFocusScope(
    <>
      {/* Schedule Selection Modal */}
      <ScheduleSelectionModal
        isOpen={showScheduleModal}
        onClose={() => { chooserDismissed.current = true; setShowScheduleModal(false); }}
        schedules={userSchedules}
        onScheduleSelect={handleModalScheduleSelect}
        primaryScheduleId={primaryScheduleId || undefined}
        isViewingSchedule={!!schedule}
        onWebRegImport={handleWebRegImport}
        currentSemester={catalogTerm}
        operationContextKey={creationContextKey}
        isLoadingSchedules={schedulesDirectoryLoading}
        schedulesLoadError={schedulesDirectoryError}
        onRetrySchedules={() => { void fetchUserSchedules().catch(() => { /* Bound loader owns feedback. */ }); }}
      />

      {/* Warning Indicator */}
      <WarningIndicator
        warnings={warnings}
        onDismissWarning={dismissWarning}
        onDismissAll={dismissAllWarnings}
      />

      <RotatingTips
        dismissible
        className="fixed bottom-4 left-4 z-40 w-[min(22rem,calc(100vw-2rem))] md:bottom-5 md:left-[calc(4.5rem+1.25rem)]"
      />

      <div className="ss-scheduling-workspace h-full min-h-0">
        <div ref={containerRef} className={`ss-scheduling-workspace__frame relative flex h-full min-h-0 overflow-hidden text-[0.875rem] ${isDragging ? 'select-none' : ''}`}>
          {/* Dragging overlay */}
          {isDragging && (
            <div className="fixed inset-0 z-50 cursor-col-resize pointer-events-none" />
          )}

          {userId && (schedule?.user_id === userId) && (
            <>
              {/* Left Panel - Hidden on mobile */}
              <div className="hidden md:block ml-0" style={{ width: `${Math.round(leftPanelWidth * 0.75)}px` } as React.CSSProperties}>
                <LeftPanel
                  isActive={!isMobile && !showMobilePreferences}
                  scheduleId={schedule?.schedule_id ?? ''}
                  onClassAdd={handleClassAdd}
                    additionUndo={additionUndo.feedback}
                  onClassRemove={handleClassRemove}
                  onClassPin={handleClassPin}
                  pinnedClasses={pinnedClasses}
                  selectedCourses={selectedCourses}
                  supabase={supabase}
                  userId={userId}
                  preferences={preferences}
                  preferencesOwnerId={preferencesOwner}
                  onPreferencesPreviewChange={setPreferences}
                  onPreferencesChange={handlePreferencesChange}
                  onCourseListAdd={handleCourseListAdd}
                  onCourseListRemove={handleCourseListRemove}
                  onSectionChange={handleSectionChange}
                  onQuantityChange={handleQuantityChange}
                  onAllowedSectionsChange={handleAllowedSectionsChange}
                  onCourseListConfigChange={handleCourseListConfigChange}
                  onOpenCourseConfig={handleManualCourseConfigOpen}
                  onOpenCourseListConfig={handleManualCourseListConfigOpen}
                  generateOnlyOpenSections={generateOnlyOpenSections}
                  onGenerateOnlyOpenSectionsChange={setGenerateOnlyOpenSections}
	                  customActivities={customActivities}
	                  customActivityDraft={customActivityDraft}
                  availabilityOverlayEnabled={availabilityOverlayEnabled}
                  onAvailabilityOverlayEnabledChange={setAvailabilityOverlayEnabled}
                  onCustomActivityAdd={handleCustomActivityAdd}
	                  onCustomActivityRemove={handleCustomActivityRemove}
	                  onRegenerate={regenerateSchedulesNow}
                  canRegenerate={generationReady && selectedCourses.length > 0 && !isGenerating}
                  isSaving={isSavingSchedule || isSavingGeneratedSchedule}
                  hasUnsavedChanges={hasUnsavedWorkspaceChanges}
                  currentSemester={catalogTerm}
                  onSemesterChange={handleSemesterChange}
                  isChangingSemester={semesterChange.isPending}
                  semesterChangeError={semesterChange.error}
                  currentSchedule={displayedScheduleCourses}
                  completedCourseBaseIds={completedCourseBaseIds}
                // Debug: Log the canRegenerate condition
                // canRegenerate={(() => { scheduleDebug('LeftPanel canRegenerate:', selectedCourses.length > 0); return selectedCourses.length > 0; })()}
                />
              </div>
              {/* Resize handle - Hidden on mobile */}
              <div className="hidden md:block">
                <ResizeHandle side="left" />
              </div>
            </>
          )}

          <div className="ss-scheduling-workspace__main flex-1 flex min-h-0 flex-col text-[0.9rem] min-w-[300px]">
            {/* Header */}
            <div className="ss-scheduling-workspace__header border-b p-3 lg:p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0 flex-1">
                  {isEditingName ? (
                    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                        <input
                          type="text"
                          value={tempName}
                          onChange={(e) => {
                            setTempName(e.target.value);
                            markAsChanged();
                          }}
                          className="min-h-12 text-lg lg:text-xl font-bold bg-white dark:bg-slate-800 text-gray-900 dark:text-white rounded-control px-2.5 py-1.5 border border-violet-200 dark:border-violet-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          onKeyPress={(e) => e.key === 'Enter' && handleCommitName()}
                          onBlur={handleCommitName}
                          autoFocus
                          aria-label="Schedule Name"
                        />
                        <button
                          onClick={handleCommitName}
                          className="text-green-500 hover:text-green-400 p-1.5"
                          title="Save Name"
                        >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-3">
                      {/* Show star for generated schedules OR if current schedule is primary */}
                      <button
                        onClick={() => {
                          if (isViewingSelectedSchedule) {
                            // If viewing selected schedule, make it primary
                            handleMakePrimary(0); // Use 0 since we're viewing a saved schedule
                          } else {
                            // If viewing generated schedule, make it primary
                            handleMakePrimary(currentScheduleIndex);
                          }
                        }}
                        disabled={!hydration.canWrite() || primaryScheduleId === schedule?.schedule_id || isSavingSchedule || isSavingGeneratedSchedule || semesterChange.isPending || (!isViewingSelectedSchedule && generatedSaveDisabled)}
                        className={`p-1 transition-colors ${primaryScheduleId === schedule?.schedule_id
                          ? 'text-yellow-400 cursor-not-allowed'
                          : 'text-yellow-500 hover:text-yellow-400'
                          }`}
                        title={
                          primaryScheduleId === schedule?.schedule_id
                            ? 'This is already your primary schedule'
                            : 'Make this your primary schedule'
                        }
                      >
                        <svg
                          className="w-5 h-5"
                          fill={primaryScheduleId === schedule?.schedule_id ? "currentColor" : "none"}
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                        </svg>
                      </button>

                      <div className="flex min-w-0 items-center gap-1 sm:gap-2">
                        <h1
                          ref={scheduleTitleRef}
                          tabIndex={-1}
                          className="truncate text-xl font-bold text-gray-900 transition-colors hover:text-violet-700 lg:text-3xl dark:text-white dark:hover:text-violet-300"
                          onClick={() => { if (schedule?.user_id === user?.id) setIsEditingName(true); }}
                        >
                          {schedule?.name || 'New Schedule'}
                        </h1>
                        {schedule?.user_id === user?.id && (
                          <button
                            className="flex min-h-12 min-w-12 shrink-0 items-center justify-center text-gray-600 hover:text-violet-700 lg:min-h-10 lg:min-w-10 dark:text-gray-400 dark:hover:text-violet-300"
                            onClick={() => setIsEditingName(true)}
                            title="Rename schedule"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                        )}

                      </div>
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    {schedule?.term_id ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200">
                        {schedule.term_id.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')}
                      </span>
                    ) : null}
                    <span className="text-xs text-gray-700 dark:text-gray-300 flex items-center">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 text-center"></span>
                      {selectedCourses.reduce((total, course) => {
                        if (course.isCourseList) {
                          return total + (course.quantity || 1);
                        }
                        return total + 1;
                      }, 0)} courses selected
                    </span>

                    <button
                      type="button"
                      aria-expanded={showMobileAdditionalOptions}
                      aria-controls="mobile-schedule-additional-options"
                      onClick={() => setShowMobileAdditionalOptions((isOpen) => !isOpen)}
                      className="inline-flex min-h-12 items-center gap-1.5 rounded-control px-2.5 text-xs font-semibold text-[var(--ss-text-muted)] transition-colors hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] lg:hidden motion-reduce:transition-none"
                    >
                      Additional options
                      <ChevronDownIcon
                        aria-hidden="true"
                        className={`h-4 w-4 transition-transform motion-reduce:transition-none ${showMobileAdditionalOptions ? 'rotate-180' : ''}`}
                      />
                    </button>

                    {/* Save Button - Only show when NOT viewing selected schedule */}
                    {!isViewingSelectedSchedule && schedule?.user_id === user?.id && (
                      <div className="relative hidden lg:block">
                        <button
                          onClick={() => {
                            handleSaveGeneratedSchedule(currentScheduleIndex);
                            setShowSavePrompt(false); // Hide prompt when user saves
                          }}
                          className="flex min-h-12 items-center gap-2 rounded-control bg-green-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 md:min-h-0"
                          title="Save schedule"
                          disabled={isSavingSchedule || isSavingGeneratedSchedule || (!isViewingSelectedSchedule && generatedSaveDisabled)}
                          aria-busy={isSavingSchedule || isSavingGeneratedSchedule}
                        >
                          <ArrowDownOnSquareIcon className="h-4 w-4" />
                          {isSavingGeneratedSchedule ? 'Saving…' : 'Save'}
                        </button>

                        {/* Save prompt that appears after 5 seconds of viewing */}
                        {showSavePrompt && (
                          <div
                            className="absolute left-0 top-full z-50 mt-2 cursor-pointer whitespace-nowrap rounded-control bg-blue-600 px-3 py-2 text-xs text-white shadow-lg animate-pulse"
                            role="tooltip"
                            onClick={() => setShowSavePrompt(false)}
                            title="Click to dismiss"
                          >
                            <div className="flex items-center gap-2">
                              <FunctionalIcon name="save" />
                              <span>Save this schedule?</span>
                            </div>
                            <div className="absolute bottom-full left-4 h-0 w-0 border-b-4 border-l-4 border-r-4 border-transparent border-b-blue-600"></div>
                          </div>
                        )}
                      </div>
                    )}

                  </div>

                  {schedule?.user_id === user?.id ? (
                    <div className="mt-3 grid grid-cols-2 gap-2 lg:hidden">
                      {!isViewingSelectedSchedule ? (
                        <button
                          onClick={() => {
                            handleSaveGeneratedSchedule(currentScheduleIndex);
                            setShowSavePrompt(false);
                          }}
                          className="flex min-h-12 items-center justify-center gap-2 rounded-control bg-green-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] motion-reduce:transition-none"
                          title="Save schedule"
                          disabled={isSavingSchedule || isSavingGeneratedSchedule || (!isViewingSelectedSchedule && generatedSaveDisabled)}
                          aria-busy={isSavingSchedule || isSavingGeneratedSchedule}
                        >
                          <ArrowDownOnSquareIcon className="h-5 w-5" />
                          {isSavingGeneratedSchedule ? 'Saving…' : 'Save'}
                        </button>
                      ) : null}
                      <button
                        onClick={() => setShowMobilePreferences(true)}
                        className={`flex min-h-12 items-center justify-center gap-2 rounded-control bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] motion-reduce:transition-none ${isViewingSelectedSchedule ? 'col-span-2' : ''}`}
                        title="Course Planner"
                      >
                        <AcademicCapIcon className="h-5 w-5" />
                        Course Planner
                      </button>
                    </div>
                  ) : null}
                  {generatedSaveOutcome.outcome && (hydration.phase === 'ready' || generatedSaveOutcome.outcome.state === 'pending') && (
                    <p role={generatedSaveOutcome.outcome.state === 'error' ? 'alert' : 'status'}
                      className={`mt-2 text-sm font-medium ${generatedSaveOutcome.outcome.state === 'error' ? 'text-[var(--ss-critical)]' : 'text-[var(--ss-text-muted)]'}`}>
                      {generatedSaveOutcome.outcome.state === 'pending' ? 'Saving' : generatedSaveOutcome.outcome.state === 'error' ? 'Could not save' : 'Saved'}
                      {generatedSaveOutcome.outcome.candidate !== null ? ` Option ${generatedSaveOutcome.outcome.candidate}` : ' schedule'}
                      {` to “${generatedSaveOutcome.outcome.scheduleName}” · ${generatedSaveOutcome.outcome.termLabel}`}
                      {generatedSaveOutcome.outcome.makePrimary ? ' · Make Primary' : ''}
                      {generatedSaveOutcome.outcome.state === 'error' ? '. Please try again.' : null}
                      {generatedSaveOutcome.outcome.savedAt ? <> · <time dateTime={generatedSaveOutcome.outcome.savedAt}>{new Date(generatedSaveOutcome.outcome.savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</time></> : null}
                    </p>
                  )}
                </div>
                <div
                  id="mobile-schedule-additional-options"
                  className={`${showMobileAdditionalOptions ? 'flex' : 'hidden'} w-full flex-col items-start gap-2 lg:flex lg:w-auto lg:items-end`}
                >
                  {schedule?.user_id === user?.id && (
                    <div className="w-full lg:w-auto">
                      <div className="flex flex-nowrap items-center justify-start gap-2 lg:flex-wrap lg:justify-end">
                        <div
                          role="group"
                          aria-label="Schedule sharing"
                          className={`inline-flex min-h-12 items-center rounded-full border text-sm font-medium md:min-h-10 ${schedule?.is_public
                            ? 'border-[var(--ss-positive)] bg-[var(--ss-positive-bg)] text-[var(--ss-positive)]'
                            : 'border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] text-[var(--ss-text)]'
                          }`}
                        >
                        <button
                          type="button"
                          onClick={() => void handleCopyScheduleLink()}
                          className="inline-flex min-h-12 items-center gap-2 rounded-l-full px-3 transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ss-focus)] md:min-h-10 lg:pl-4 motion-reduce:transition-none dark:hover:bg-white/5"
                          title={schedule?.is_public ? 'Copy public schedule link' : 'Make this schedule public to copy its link'}
                        >
                          <ShareIcon className="h-4 w-4" />
                          <span>{schedule?.is_public ? 'Public' : 'Private'}</span>
                        </button>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={Boolean(schedule?.is_public)}
                          aria-label={`Make schedule ${schedule?.is_public ? 'private' : 'public'}`}
                          onClick={handleTogglePublic}
                          className="inline-flex min-h-12 items-center rounded-r-full px-2 pr-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--ss-focus)] md:min-h-10"
                          title={`Make schedule ${schedule?.is_public ? 'private' : 'public'}`}
                        >
                          <span className={`inline-flex h-5 w-9 items-center rounded-full transition-colors motion-reduce:transition-none ${schedule?.is_public ? 'bg-[var(--ss-positive)]' : 'bg-[var(--ss-text-muted)]'}`}>
                            <span className={`h-4 w-4 rounded-full bg-white transition-transform motion-reduce:transition-none ${schedule?.is_public ? 'translate-x-4' : 'translate-x-1'}`} />
                          </span>
                        </button>
                        </div>

                      {/* Create a copy button - Only show when NOT viewing selected schedule */}
                      {!isViewingSelectedSchedule && schedule?.user_id === user?.id && (
                        <Tooltip content="Create a copy" side="bottom">
                          <IconButton
                            label="Create a copy of this schedule"
                            variant="quiet"
                            onClick={handleSaveToMySchedules}
                            loading={scheduleCopy.status === 'creating'}
                            disabled={scheduleCopy.isPending || !copyPayload || scheduleCopy.status === 'confirmed' || scheduleCopy.status === 'unconfirmed'}
                            className="hover:text-blue-600 dark:hover:text-blue-400"
                          >
                            <DocumentDuplicateIcon className="h-5 w-5" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* When viewing selected schedule: Show Open WebReg as a button */}
                      {isViewingSelectedSchedule && (
                        <button
                          onClick={handleExportToWebReg}
                          className="hidden items-center gap-2 rounded-control bg-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 lg:flex lg:px-4 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                          title="Open WebReg"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                          </svg>
                          <span>Open WebReg</span>
                        </button>
                      )}

                      {/* Export Dropdown - Only show when viewing selected schedule */}
                      {isViewingSelectedSchedule && (
                        <div className="relative export-dropdown">
                          <button
                            onClick={() => setShowExportDropdown(!showExportDropdown)}
                            className="flex min-h-12 items-center justify-center gap-2 rounded-full border border-[var(--ss-border)] bg-[var(--ss-surface)] px-2.5 py-1.5 text-[var(--ss-text)] shadow-e1 transition-[background-color,box-shadow] hover:bg-[var(--ss-surface-subtle)] hover:shadow-e2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] lg:min-h-10 motion-reduce:transition-none"
                            title="Export Schedule"
                            aria-label="Open schedule export options"
                          >
                            <span className="flex -space-x-2" aria-hidden="true"><span className="grid h-8 w-8 place-items-center rounded-full border-2 border-[var(--ss-surface)] bg-sky-500 text-white"><LinkIcon className="h-4 w-4"/></span><span className="grid h-8 w-8 place-items-center rounded-full border-2 border-[var(--ss-surface)] bg-violet-500 text-white"><CalendarDaysIcon className="h-4 w-4"/></span><span className="grid h-8 w-8 place-items-center rounded-full border-2 border-[var(--ss-surface)] bg-white p-1"><GoogleCalendarIcon className="h-6 w-6" /></span><span className="grid h-8 w-8 place-items-center rounded-full border-2 border-[var(--ss-surface)] bg-gradient-to-br from-amber-400 via-rose-500 to-violet-600 text-white"><PhotoIcon className="h-4 w-4"/></span></span>
                            <span className="hidden text-xs font-semibold xl:inline">Export</span>
                            <ChevronDownIcon className="h-4 w-4" />
                          </button>

                          {showExportDropdown && (
                            <div className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-feature border border-[var(--ss-border)] bg-[var(--ss-surface)] p-2 shadow-e3">
                              {/* Menu Items */}
                              <div className="py-1">
                                <button
                                  onClick={() => {
                                    handleExportToWebReg();
                                    setShowExportDropdown(false);
                                  }}
                                  className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 lg:hidden dark:hover:bg-gray-700"
                                >
                                  <AcademicCapIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                                  <span className="text-xs font-medium text-gray-900 dark:text-white">Open WebReg</span>
                                </button>
                                <button
                                  onClick={() => {
                                    void handleCopyScheduleLink();
                                    setShowExportDropdown(false);
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3 group"
                                >
                                  <div className="text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white">
                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                    </svg>
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-xs font-medium text-gray-900 dark:text-white">
                                      Copy Schedule Link
                                    </div>
                                    <div className="text-[11px] text-gray-600 dark:text-gray-400">
                                      Share your schedule with others
                                    </div>
                                  </div>
                                </button>

                                <button
                                  onClick={async () => {
                                    await handleCopyIcsSubscribeLink();
                                    setShowExportDropdown(false);
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3 group"
                                >
                                  <div className="text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white">
                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-xs font-medium text-gray-900 dark:text-white">
                                      Copy Calendar Subscribe Link (ICS)
                                    </div>
                                    <div className="text-[11px] text-gray-600 dark:text-gray-400">
                                      Use in Google Calendar → From URL
                                    </div>
                                  </div>
                                </button>

                                <button
                                  onClick={() => {
                                    handleExportToGoogleCalendar();
                                    setShowExportDropdown(false);
                                  }}
                                  disabled={isExporting}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3 group disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <div className="text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white">
                                    {isExporting ? (
                                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b border-gray-700 dark:border-gray-300"></div>
                                    ) : (
                                      <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-xs font-medium text-gray-900 dark:text-white">
                                      {isExporting ? 'Exporting...' : 'Export to Google Calendar'}
                                    </div>
                                    <div className="text-[11px] text-gray-600 dark:text-gray-400">
                                      Add courses to your Scarlet Sync calendar
                                    </div>
                                  </div>
                                </button>

                                <button
                                  onClick={() => {
                                    handleExportToImage();
                                    setShowExportDropdown(false);
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-3 group"
                                >
                                  <div className="text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white">
                                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l-1-1m-6 6h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-xs font-medium text-gray-900 dark:text-white">
                                      Export as Image
                                    </div>
                                    <div className="text-[11px] text-gray-600 dark:text-gray-400">
                                      Download the schedule as a PNG image
                                    </div>
                                  </div>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                        <FriendOverlayControl
                          friends={friendsWithSchedules}
                          showOwnSchedule={showOwnScheduleForOverlay}
                          onShowOwnScheduleChange={setShowOwnScheduleForOverlay}
                          ownPictureUrl={comparisonProfile?.picture}
                          ownName={comparisonProfile?.name || user?.email?.split('@')[0].replace(/[._-]+/g, ' ') || 'You'}
                          selectedFriendIds={selectedFriendIdsForOverlay}
                          open={showFriendOverlayDropdown}
                          onOpenChange={setShowFriendOverlayDropdown}
                          onSelectedFriendIdsChange={setSelectedFriendIdsForOverlay}
                        />
                      </div>
                    </div>
                  )}
                  {schedule && schedule.user_id !== user?.id && (
                    <button
                      onClick={handleSaveToMySchedules}
                      aria-busy={scheduleCopy.status === 'creating'}
                      disabled={scheduleCopy.isPending || !copyPayload || scheduleCopy.status === 'confirmed' || scheduleCopy.status === 'unconfirmed'}
                      className="flex items-center space-x-2 px-2.5 lg:px-3 py-1.5 bg-blue-600 text-white rounded-control hover:bg-blue-700 transition-colors font-medium text-xs"
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Save Copy</span>
                      <span className="sm:hidden">Copy</span>
                    </button>
                  )}
                  </div>
              </div>
            </div>

            {scheduleCopy.status === 'creating' && <p role="status" className="px-3 py-2 text-sm text-content-muted">Creating copy…</p>}
            {scheduleCopy.error && <p role="alert" className="px-3 py-2 text-sm font-medium text-[var(--ss-critical)]">{scheduleCopy.error}</p>}
            {scheduleCopy.status === 'confirmed' && scheduleCopy.copyId && (
              <p role="status" className="px-3 py-2 text-sm text-content-muted">Copy created. <a className="underline" href={`/scheduling?id=${encodeURIComponent(scheduleCopy.copyId)}`}>Open copy</a></p>
            )}

            {/* Saved-schedule management is irrelevant while previewing an unsaved generation. */}
            {isViewingSelectedSchedule && (
              <div className="border-b border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] px-3 py-2 lg:px-4">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={blankCreation.createNewSchedule}
                    disabled={blankCreation.isPending || Boolean(scheduleId && !hydration.canWrite())}
                    aria-busy={blankCreation.isPending}
                    className="flex min-h-12 items-center gap-1.5 rounded-control border border-[var(--ss-focus)] bg-[var(--ss-brand-bg)] px-3 py-2 text-xs font-semibold text-[var(--ss-focus)] transition-colors hover:brightness-95 md:min-h-9 motion-reduce:transition-none"
                    title="Create New Schedule"
                  >
                    <PlusIcon className="h-4 w-4" />
                    <span>{blankCreation.isPending ? 'Please wait…' : 'New Schedule'}</span>
                  </button>
                  {blankCreation.error && <p role="alert" className="text-xs text-red-600 dark:text-red-400">{blankCreation.error}</p>}
                  {blankCreation.status && <p role="status" className="text-xs text-[var(--ss-text)]">{blankCreation.status}</p>}
                  <button
                    type="button"
                    onClick={() => {
                      setShowScheduleModal(true);
                      setIsModalOpenedManually(true);
                      setShowMobilePreferences(false);
                    }}
                    className="flex min-h-12 items-center gap-1.5 rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface)] px-3 py-2 text-xs font-semibold text-[var(--ss-text)] transition-colors hover:bg-[var(--ss-surface-subtle)] md:min-h-9 motion-reduce:transition-none"
                    title="Open all saved schedules"
                  >
                    <CalendarDaysIcon className="h-4 w-4" />
                    <span>Saved schedules</span>
                  </button>
                  <span aria-hidden="true" className="mx-0.5 hidden h-5 w-px bg-[var(--ss-border)] sm:block" />
                  {schedulesForCurrentSemester.map((s: any) => (
                    <button
                      key={s.schedule_id}
                      onClick={() => {
                        router.push(`/scheduling?id=${s.schedule_id}`);
                      }}
                      aria-current={s.schedule_id === schedule?.schedule_id ? 'page' : undefined}
                      className={`min-h-12 rounded-full border px-3 py-2 text-xs font-medium transition-colors md:min-h-8 motion-reduce:transition-none ${s.schedule_id === schedule?.schedule_id
                        ? 'border-[var(--ss-focus)] bg-[var(--ss-brand-bg)] text-[var(--ss-focus)]'
                        : 'border-[var(--ss-border)] bg-[var(--ss-surface)] text-[var(--ss-text-muted)] hover:text-[var(--ss-text)]'
                      }`}
                      title={`Open ${s.name || 'Schedule'}`}
                    >
                      {s.name || 'Untitled Schedule'}
                    </button>
                  ))}
                  {schedule?.user_id === userId ? <ScheduleHistoryControl
                    key={schedule.schedule_id}
                    proposal={activeScheduleProposal}
                    revisions={scheduleRevisions}
                    loading={proposalLoading}
                    applying={proposalApplying}
                    onApply={handleApplyScheduleProposal}
                    onDismiss={handleDismissScheduleProposal}
                    onRestore={handleRestoreScheduleRevision}
                  /> : null}
                </div>
              </div>
            )}

            <div className="px-3 pt-2 lg:px-4">
              <OwnedHydrationRecovery key={JSON.stringify([user?.id, scheduleId, configurationSaveRouteKey])}
                returnFocusRef={scheduleTitleRef}
                phase={hydration.phase} targetTerm={hydration.targetTerm} error={hydration.error}
                onUseSaved={() => { void hydration.load('review'); }} />
              {!showMobilePreferences && generationFeedback}
            </div>

            {/* Main Content */}
            <div className="ss-scheduling-workspace__content min-h-0 flex-1 overflow-y-auto overscroll-contain">
              <ScheduleView
                ref={scheduleViewRef}
                schedule={proposalSchedulePreview.courses}
                selectedCourseCount={selectedCourses.length}
                courseChangeStates={proposalSchedulePreview.changeStates}
                friendPresenceBySection={displayedTerm === catalogTerm ? friendPresenceBySection : {}}
                onNextSchedule={() => {
                  if (schedule?.user_id !== user?.id) return;
                  additionUndo.retire();
                  let changed = false;
                  if (isViewingSelectedSchedule && generatedSchedules.length > 0) {
                    setIsViewingSelectedSchedule(false);
                    setCurrentScheduleIndex(0);
                    changed = true;
                  } else if (!isViewingSelectedSchedule && Math.min(currentScheduleIndex + 1, generatedSchedules.length - 1) !== currentScheduleIndex) {
                    setCurrentScheduleIndex(index => Math.min(index + 1, generatedSchedules.length - 1));
                    changed = true;
                  }
                  if (changed) { setHasUnsavedChanges(true); markAsChanged(); }
                }}
                onPrevSchedule={() => {
                  if (schedule?.user_id !== user?.id) return;
                  additionUndo.retire();
                  let changed = false;
                  if (isViewingSelectedSchedule && generatedSchedules.length > 0) {
                    setIsViewingSelectedSchedule(false);
                    setCurrentScheduleIndex(generatedSchedules.length - 1);
                    changed = true;
                  } else if (!isViewingSelectedSchedule && currentScheduleIndex === 0) {
                    setIsViewingSelectedSchedule(true);
                    changed = true;
                  } else if (!isViewingSelectedSchedule && Math.max(currentScheduleIndex - 1, 0) !== currentScheduleIndex) {
                    setCurrentScheduleIndex(index => Math.max(index - 1, 0));
                    changed = true;
                  }
                  if (changed) { setHasUnsavedChanges(true); markAsChanged(); }
                }}
                onHomeSchedule={() => {
                  if (schedule?.user_id !== user?.id) return;
                  additionUndo.retire();
                  if (!isViewingSelectedSchedule || currentScheduleIndex !== -1) {
                    setIsViewingSelectedSchedule(true);
                    setCurrentScheduleIndex(-1);
                    setHasUnsavedChanges(true);
                    markAsChanged();
                  }
                }}
                currentScheduleIndex={isViewingSelectedSchedule ? -1 : currentScheduleIndex}
                totalSchedules={schedule?.user_id === user?.id ? Math.max(generatedSchedules.length, 1) : 1}
                totalSchedulesGenerated={totalSchedulesGenerated}
                allSectionsByCourseId={Object.fromEntries((!isViewingSelectedSchedule && generationBelongsToWorkspace ? generationIdentity.input.groupedCourses : selectedCourses).map(course => [course.id, course.sections]))}
                onSaveSchedule={isSavingSchedule || isSavingGeneratedSchedule || semesterChange.isPending ? undefined : isViewingSelectedSchedule ? handleSaveSelectedSchedule : generatedSaveDisabled ? undefined : handleSaveGeneratedSchedule}
                onMakePrimary={!isViewingSelectedSchedule && generatedSaveDisabled ? undefined : handleMakePrimary}
                isSelectedSchedule={isViewingSelectedSchedule}
                onGenerateRandomSchedule={handleGenerateRandomSchedule}
                onTimeBlockSelect={handleTimeBlockSelect}
                onFindClassesInTimeBlock={handleFindClassesInTimeBlock}
                onMakeTimeBlockAvailable={handleMakeTimeBlockAvailable}
                showAvailabilityOverlay={availabilityOverlayEnabled}
                preferences={!isViewingSelectedSchedule && generationBelongsToWorkspace ? generationIdentity.input.preferences : memoizedPreferences}
                currentScheduleScore={!isViewingSelectedSchedule ? generatedSchedules[currentScheduleIndex]?.score : undefined}
                onCourseConfigRequest={!isViewingSelectedSchedule && !generatedResultsCurrent ? undefined : handleScheduleCourseConfigRequest}
                onExportToGoogleCalendar={!isViewingSelectedSchedule && !generatedResultsCurrent ? undefined : exportToGoogleCalendar}
                conflicts={overlapConflicts}
                onShowConflicts={() => setShowOverlapModal(true)}
                exportMode={isExportMode}
                isMobile={isMobile}
                semester={displayedTerm}
                noScheduleReasons={noScheduleReasons}
                onOpenNoScheduleModal={canReviewGeneration ? () => setShowNoScheduleModal(true) : undefined}
                regenIndicatorVisible={showRegenerationIndicator}
                coursesFromCourseLists={coursesFromCourseLists}
                overlayFriendSchedules={displayedTerm === catalogTerm ? overlayFriendSchedules : []}
                showOwnSchedule={showOwnScheduleForOverlay}
                showDetailedCourseInformation={false}
                headerActions={workspaceTab === 'schedule' ? scheduleViewControl : undefined}
                workspaceTabs={
                  <Tabs
                    label="Scheduling workspace views"
                    activeId={workspaceTab}
                    onChange={id => setWorkspaceTab(id as typeof workspaceTab)}
                    className="max-w-full border-b-0"
                    items={[
                      { id: 'schedule', label: 'Schedule', icon: <CalendarDaysIcon className="h-4 w-4" /> },
                      { id: 'information', label: 'Information', icon: <InformationCircleIcon className="h-4 w-4" /> },
                      { id: 'generations', label: 'Generations', icon: <ChartBarIcon className="h-4 w-4" />, badge: totalSchedulesGenerated > 100 ? '100+' : totalSchedulesGenerated },
                    ]}
                  />
                }
                contentOverride={
                  workspaceTab === 'schedule'
                    && isViewingSelectedSchedule
                    && schedule?.user_id === userId
                    && (schedule?.classes?.length ?? 0) === 0
                    && selectedCourses.length === 0
                    && selectedCoursesList.length === 0
                    && customActivities.length === 0 ? (
                    <ScheduleGettingStarted
                      termLabel={SEMESTERS.find((semester) => semester.id === (schedule?.term_id || activeSemester))?.label || 'current term'}
                      onOpenPlanner={openCoursePlanner}
                    />
                  ) : workspaceTab === 'schedule' && schedulePresentation === 'finals' ? (
                    <FinalsScheduleGrid courses={proposalSchedulePreview.courses} termId={displayedTerm} />
                  ) : workspaceTab === 'schedule' && schedulePresentation === 'list' ? (
                    <ScheduleCourseList courses={proposalSchedulePreview.courses} onOpenCourse={openCourseInsight} />
                  ) : workspaceTab === 'information' ? (
                    <div role="tabpanel" aria-label="Information">
                      <ScheduleInsightsPanel
                        courses={displayedScheduleCourses.filter(
                          course => !course.id.startsWith('custom:') && course.department !== 'CUSTOM',
                        )}
                        focusedCourseId={focusedInsightCourseId}
                        onAskAI={askScheduleAI}
                        semester={displayedTerm}
                      />
                    </div>
                  ) : workspaceTab === 'generations' ? (
                    <div role="tabpanel" aria-label="Generations">
                      <ScheduleGenerationsPanel
                        generatedSchedules={generatedSchedules}
                        currentScheduleIndex={currentScheduleIndex}
                        totalSchedulesGenerated={totalSchedulesGenerated}
                        onScheduleSelect={index => {
                          handleScheduleSelect(index);
                          setWorkspaceTab('schedule');
                        }}
                        onSortChange={(key: 'score_desc' | 'score_asc') => {
                          additionUndo.retire();
                          const sorted = [...generatedSchedules].sort((a, b) => key === 'score_desc' ? b.score - a.score : a.score - b.score);
                          const changed = currentScheduleIndex !== 0 || sorted.some((entry, index) => entry !== generatedSchedules[index]);
                          setGeneratedSchedules(previous => [...previous].sort((a, b) => key === 'score_desc' ? b.score - a.score : a.score - b.score));
                          setCurrentScheduleIndex(0);
                          if (changed) { setHasUnsavedChanges(true); markAsChanged(); }
                        }}
                        onRegenerate={regenerateSchedulesNow}
                        canRegenerate={generationReady && selectedCourses.length > 0 && !isGenerating}
                        preferences={generationBelongsToWorkspace ? generationIdentity.input.preferences : memoizedPreferences}
                        friendSchedulesForScoring={generationBelongsToWorkspace ? generationIdentity.input.friendSchedulesForScoring : undefined}
                        generationNotice={courseListGenerationNotice || undefined}
                        noScheduleReasons={noScheduleReasons || undefined}
                        onOpenNoScheduleModal={canReviewGeneration ? () => setShowNoScheduleModal(true) : undefined}
                      />
                    </div>
                  ) : undefined
                }
              />
            </div>
          </div>

          {/* The canonical portal keeps the planner above page chrome and owns focus/scroll. */}
          <Modal isOpen={showMobilePreferences} onClose={() => setShowMobilePreferences(false)} title="Course Planner" size="lg">
            <div className="mb-3">{generationFeedback}</div>
            <button type="button" className="ss-button mb-3 min-h-12" data-variant="ai" onClick={() => { setShowMobilePreferences(false); openAssistant({ startNewChat: true, source: 'scheduling-mobile-planner' }); }}>Ask Scarlet Sync</button>
            <div className="h-[calc(100dvh-16rem)] min-h-64 overflow-hidden">
                  <LeftPanel
                    isActive={showMobilePreferences}
                    scheduleId={schedule?.schedule_id ?? ''}
                    onClassAdd={handleClassAdd}
                    additionUndo={additionUndo.feedback}
                    onClassRemove={handleClassRemove}
                    onClassPin={handleClassPin}
                    pinnedClasses={pinnedClasses}
                    selectedCourses={selectedCourses}
                    supabase={supabase}
                    userId={userId}
                    preferences={preferences}
                    preferencesOwnerId={preferencesOwner}
                    onPreferencesPreviewChange={setPreferences}
                    onPreferencesChange={handlePreferencesChange}
                    onCourseListAdd={handleCourseListAdd}
                    onCourseListRemove={handleCourseListRemove}
                    onSectionChange={handleSectionChange}
                    onQuantityChange={handleQuantityChange}
                    onAllowedSectionsChange={handleAllowedSectionsChange}
                    onCourseListConfigChange={handleCourseListConfigChange}
                    onOpenCourseConfig={handleManualCourseConfigOpen}
                    onOpenCourseListConfig={handleManualCourseListConfigOpen}
                    generateOnlyOpenSections={generateOnlyOpenSections}
                    onGenerateOnlyOpenSectionsChange={setGenerateOnlyOpenSections}
	                    customActivities={customActivities}
	                    customActivityDraft={customActivityDraft}
                    availabilityOverlayEnabled={availabilityOverlayEnabled}
                    onAvailabilityOverlayEnabledChange={setAvailabilityOverlayEnabled}
                    onCustomActivityAdd={handleCustomActivityAdd}
	                    onCustomActivityRemove={handleCustomActivityRemove}
	                    onRegenerate={regenerateSchedulesNow}
                    canRegenerate={generationReady && selectedCourses.length > 0 && !isGenerating}
                    isMobile={true}
                    currentSemester={catalogTerm}
                    onSemesterChange={handleSemesterChange}
                    isChangingSemester={semesterChange.isPending}
                    semesterChangeError={semesterChange.error}
                    isSaving={isSavingSchedule || isSavingGeneratedSchedule}
                    hasUnsavedChanges={hasUnsavedWorkspaceChanges}
                    completedCourseBaseIds={completedCourseBaseIds}
                  />
            </div>
          </Modal>

        </div>
      </div>

      <CourseConfigurationModal
        isOpen={!!activeCourseForConfig}
        onClose={closeCourseConfig}
        course={activeCourseForConfig}
        onSave={(allowedSections) => {
          if (courseConfigState.courseId) {
            handleAllowedSectionsChange(courseConfigState.courseId, allowedSections);
          }
        }}
        focusSectionId={courseConfigState.focusSectionId}
        returnFocusRef={scheduleTitleRef}
        openSectionsTermId={openSectionsTermId}
      />

      <CourseListConfigurationModal
        isOpen={!!activeCourseListForConfig}
        onClose={closeCourseListConfig}
        courseListGroup={activeCourseListForConfig}
        catalogReady={loadedCatalogTerm === catalogTerm}
        catalogSections={loadedCatalogTerm === catalogTerm ? allCourses : EMPTY_CATALOG_SECTIONS}
        onSave={(quantity, allowedCourses) => {
          if (courseListConfigState.groupId) {
            handleCourseListConfigChange(courseListConfigState.groupId, quantity, allowedCourses);
          }
        }}
        initialCourseId={courseListConfigState.initialCourseId}
        initialSectionId={courseListConfigState.initialSectionId}
        completedCourseBaseIds={completedCourseBaseIds}
        openSectionsTermId={openSectionsTermId}
      />

      <Modal isOpen={showSnipeDialog} onClose={() => setShowSnipeDialog(false)} title="Snipe closed classes" size="md">
        <div className="space-y-4"><p className="text-sm leading-6 text-content-muted">Choose the closed sections to monitor. Course Sniper alerts you when Rutgers reports an opening; it does not register or reserve a seat.</p><label className="flex min-h-12 items-center gap-3 rounded-control border border-line bg-surface-subtle px-3 font-semibold text-content"><input type="checkbox" className="h-4 w-4 accent-[var(--ss-brand)]" checked={selectedSnipeIndexes.length === closedDisplayedCourses.length && closedDisplayedCourses.length > 0} onChange={(event) => setSelectedSnipeIndexes(event.target.checked ? closedDisplayedCourses.map((course) => course.id.split(':').pop() || '').filter(Boolean) : [])} />All closed classes</label><div className="max-h-72 space-y-2 overflow-y-auto">{closedDisplayedCourses.map((course) => { const index = course.id.split(':').pop() || ''; return <label key={course.id} className="flex min-h-12 items-start gap-3 rounded-control border border-line px-3 py-2"><input type="checkbox" className="mt-1 h-4 w-4 accent-[var(--ss-brand)]" checked={selectedSnipeIndexes.includes(index)} onChange={(event) => setSelectedSnipeIndexes((current) => event.target.checked ? [...new Set([...current, index])] : current.filter((value) => value !== index))} /><span className="min-w-0"><strong className="block text-sm text-content">{course.name}</strong><span className="text-xs text-content-muted">{course.baseId} · Section {course.section} · Index {index}</span></span></label>; })}</div><div className="flex flex-wrap justify-between gap-2 border-t border-line pt-4"><a href="/scheduling/alerts" className="inline-flex min-h-10 items-center font-semibold text-focus underline underline-offset-4">Open Course Sniper</a><button type="button" disabled={!selectedSnipeIndexes.length || sniping} onClick={() => void addSelectedScheduleSnipes()} className="inline-flex min-h-10 items-center rounded-control border border-critical bg-critical px-4 text-sm font-semibold text-white hover:brightness-95 disabled:opacity-50">{sniping ? 'Adding…' : `Snipe ${selectedSnipeIndexes.length || ''} section${selectedSnipeIndexes.length === 1 ? '' : 's'}`}</button></div></div>
      </Modal>

      {/* Onboarding Modal */}
      <OnboardingModal isOpen={showModal} onClose={closeModal} />

      {/* No Schedule Modal */}
      <NoScheduleModal
        isOpen={showNoScheduleModal}
        onClose={() => setShowNoScheduleModal(false)}
        noScheduleReasons={noScheduleReasons}
        selectedCourses={selectedCourses}
        onReviewCourse={(groupId) => {
          const group = selectedCourses.find(course => course.id === groupId);
          if (group?.isCourseList) openCourseListConfig(groupId);
          else if (group) openCourseConfig(groupId);
        }}
        onReviewSelections={() => setShowMobilePreferences(true)}
      />
      <Toast 
        message={toast.message} 
        type={toast.type} 
        isVisible={toast.isVisible} 
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))} 
      />
      <AssistantButton context={assistantContext} />
    </>
  );
}

// Query-dependent interactive content owns its loading boundary.
export default function RoutePage() { return <RouteSuspense fallback={<RouteLoading title="Preparing your workspace" />}><SchedulingPage /></RouteSuspense>; }
